// Analytics Event Model public API
import { type AppearanceModeChangedEventProps } from '@/models/analytics/features/appearance';
import { type AssistantPanelActionEventProps } from '@/models/analytics/features/assistant-view';
import {
  type AudioFailedEventProps,
  type AudioStoppedEventProps,
  type AudioUploadedEventProps,
  type AudioWarningEventProps,
  type RecordingStopFailedEventProps,
} from '@/models/analytics/features/audio';
import { type ForcedLogoutEventProps } from '@/models/analytics/features/auth';
import {
  type BookDemoCliniciansSelectedEventProps,
  type BookDemoCountrySelectedEventProps,
  type BookDemoNextClickedEventProps,
} from '@/models/analytics/features/book-demo';
import { type CalculatorExecutedEventProps } from '@/models/analytics/features/calculators';
import {
  type ChatCreatedEventProps,
  type ChatDeletedEventProps,
  type ChatViewedEventProps,
  type EvidenceClickedEventProps,
  type FeatureToggledEventProps,
  type MessageAbortedEventProps,
  type MessageFailedEventProps,
  type MessageReceivedEventProps,
  type MessageStoppedEventProps,
  type MessageSubmittedEventProps,
  type PanelEventProps,
  type ToggleUpdatedEventProps,
} from '@/models/analytics/features/chat';
import {
  type ChatContextAddFileClickedEventProps,
  type ChatContextAddSessionClickedEventProps,
  type ChatContextAttachPatientClickedEventProps,
  type ChatMessageSentWithContextEventProps,
} from '@/models/analytics/features/chat-context';
import {
  type ClinicalCodeClickableEventProps,
  type ClinicalCodeCopiedEventProps,
} from '@/models/analytics/features/clinical-codes';
import {
  type CommsDashboardTabViewedEventProps,
  type CommsEntryPointClickedEventProps,
  type CommsSetupCompletedEventProps,
  type CommsSetupStartedEventProps,
  type CommsSetupStepAbandonedEventProps,
  type CommsSetupStepCompletedEventProps,
  type InboxFilterAppliedEventProps,
  type InboxItemUpdatedEventProps,
  type InboxItemViewedEventProps,
  type InboxSessionEndedEventProps,
  type InboxSessionStartedEventProps,
  type KbApplyButtonClickedEventProps,
  type KbApplySuccessViewedEventProps,
  type KbObjectivesTabViewedEventProps,
  type KbPreviewButtonClickedEventProps,
  type KbScanButtonClickedEventProps,
  type ObjectiveConfiguredEventProps,
  type ObjectiveRemovedEventProps,
  type ObjectiveSelectedEventProps,
  type ObjectiveViewedEventProps,
} from '@/models/analytics/features/comms';
import {
  type CommunityCreatedEventProps,
  type CommunitySearchedEventProps,
  type CommunityViewedEventProps,
} from '@/models/analytics/features/community';
import { type ComplianceEventProps } from '@/models/analytics/features/compliance';
import type {
  ContactSalesFormOpenedEventProps,
  ContactSalesFormSubmittedEventProps,
} from '@/models/analytics/features/contact-sales';
import {
  type ContextDeletedEventProps,
  type ContextFailedEventProps,
  type ContextFileDeletedEventProps,
  type ContextFileUploadedEventProps,
  type ContextFileUploadFailedEventProps,
  type ContextFileUploadStartedEventProps,
  type ContextUpdatedEventProps,
  type ContextUploadedEventProps,
} from '@/models/analytics/features/context-files';
import {
  type CPDDraftCreatedEventProps,
  type CPDDraftDeletedEventProps,
  type CPDDraftResumedEventProps,
  type CPDExportedEventProps,
  type CPDPageViewedEventProps,
  type FollowUpQuestionClickedEventProps,
} from '@/models/analytics/features/cpd';
import { type DeeplinkTriggeredEventProps } from '@/models/analytics/features/deeplink';
import {
  type DiarisedTranscriptViewedEventProps,
  type SpeakerAttributionUpdatedEventProps,
} from '@/models/analytics/features/diarisation';
import {
  type DictationCompletedEventProps,
  type DictationCsatDismissedEventProps,
  type DictationCsatSubmittedEventProps,
  type DictationErroredEventProps,
  type DictationStartedEventProps,
  type LegacyInlineDictationCompletedEventProps,
} from '@/models/analytics/features/dictation';
import {
  type DictateTourBannerCtaClickedEventProps,
  type DictationLogsSpotlightDismissedEventProps,
} from '@/models/analytics/features/dictation-logs';
import {
  type DocumentSuggestionBannerCarouselNavigatedEventProps,
  type DocumentSuggestionBannerCtaClickedEventProps,
  type DocumentSuggestionBannerDismissedEventProps,
  type DocumentSuggestionBannerShownEventProps,
  type DocumentSuggestionBannerSuppressedEventProps,
} from '@/models/analytics/features/document-suggestion-banner';
import {
  type DocumentCopiedEventProps,
  type DocumentDeletedEventProps,
  type DocumentEmailedEventProps,
  type DocumentEventProps,
  type DocumentExportedEventProps,
  type DocumentSharedEventProps,
  type DocumentSyncedEventProps,
  type DocumentTemplateQueueLimitReachedEventProps,
  type DocumentTemplateQueueReorderedEventProps,
  type DocumentUpdatedEventProps,
  type DocumentViewedEventProps,
  type FormFillingDownloadedEventProps,
  type QueuedDocumentGenerationCancelledEventProps,
  type QueuedDocumentGenerationFailedEventProps,
  type QueuedDocumentGenerationStartedEventProps,
  type QueuedDocumentTabTemplateSwappedEventProps,
  type SessionStartedWithDocumentQueueEventProps,
} from '@/models/analytics/features/documents';
import {
  type VerificationAutoModeToggledEventProps,
  type VerificationAutoPopoverActionEventProps,
  type VerificationAutoPopoverShownEventProps,
  type VerificationAutoTriggeredEventProps,
  type VerificationCompletedEventProps,
  type VerificationFailedEventProps,
  type VerificationFeedbackSubmittedEventProps,
  type VerificationOverallFeedbackSubmittedEventProps,
  type VerificationStartedEventProps,
  type VerificationSuggestedFixAppliedEventProps,
} from '@/models/analytics/features/entity-verification';
import {
  type ErrorViewedEventProps,
  type OnboardingErrorViewedEventProps,
} from '@/models/analytics/features/errors';
import {
  type CreditsConsumedEventProps,
  type EvidenceCategoryClickedEventProps,
  type EvidenceChatAbortedEventProps,
  type EvidenceChatCompletedEventProps,
  type EvidenceChatCtaClickedEventProps,
  type EvidenceChatErrorEventProps,
  type EvidenceChatSubmittedEventProps,
  type EvidenceCheckOtherSourcesClickedEventProps,
  type EvidenceContactSalesClickedEventProps,
  type EvidenceCopiedEventProps,
  type EvidenceCopyConfirmationEventProps,
  type EvidenceExperienceFeedbackDismissedEventProps,
  type EvidenceExperienceFeedbackRatingSubmittedEventProps,
  type EvidenceExperienceFeedbackSnoozedEventProps,
  type EvidenceExperienceFeedbackSubmittedEventProps,
  type EvidenceExperienceFeedbackViewedEventProps,
  type EvidenceJurisdictionChangedEventProps,
  type EvidenceLanguageChangedEventProps,
  type EvidenceLiveSuggestionsToggledEventProps,
  type EvidenceNhsContactSalesSubmittedEventProps,
  type EvidenceOnboardingDismissedEventProps,
  type EvidenceOnboardingSavedEventProps,
  type EvidenceOnboardingSkippedEventProps,
  type EvidenceOnboardingViewedEventProps,
  type EvidenceReferenceFeedbackSubmittedEventProps,
  type EvidenceReferenceScrollClickedEventProps,
  type EvidenceReferencesSectionToggledEventProps,
  type EvidenceRequestAccessClickedEventProps,
  type EvidenceResponseFeedbackSubmittedEventProps,
  type EvidenceSharedChatOpenedEventProps,
  type EvidenceShareLinkCopiedEventProps,
  type EvidenceShareModalOpenedEventProps,
  type EvidenceShareSocialClickedEventProps,
  type EvidenceSignUpWallViewedEventProps,
  type EvidenceSourceClickedEventProps,
  type EvidenceSourceControlToggledEventProps,
  type EvidenceSpecialtyChangedEventProps,
  type EvidenceSuggestionClickedEventProps,
  type EvidenceToastShownEventProps,
  type EvidenceVerbClickedEventProps,
} from '@/models/analytics/features/evidence';
import {
  type EvidencePromptsBarDismissedEventProps,
  type EvidencePromptsBarExpandedEventProps,
  type EvidencePromptsBarShownEventProps,
} from '@/models/analytics/features/evidence-prompts-bar';
import {
  type EvidenceShortcutRunEventProps,
  type EvidenceShortcutSaveToggledEventProps,
  type EvidenceShortcutSearchUsedEventProps,
  type EvidenceShortcutsOpenedEventProps,
  type EvidenceSourcesOpenedEventProps,
} from '@/models/analytics/features/evidence-shortcuts';
import {
  type EvidenceSummarySuggestionDismissedEventProps,
  type EvidenceSummarySuggestionGeneratedEventProps,
  type EvidenceSummarySuggestionShownEventProps,
} from '@/models/analytics/features/evidence-summary-suggestion';
import {
  type ExportUpdatedEventProps,
  type NotificationUpdatedEventProps,
} from '@/models/analytics/features/export-settings';
import { type FeedbackUpdatedEventProps } from '@/models/analytics/features/feedback';
import {
  type ForceUpdateDeferredEventProps,
  type ForceUpdateInitiatedEventProps,
  type ForceUpdateViewedEventProps,
} from '@/models/analytics/features/forceUpdate';
import {
  type LetterSenderAttachmentRejectedEventProps,
  type LetterSenderCcToggledEventProps,
  type LetterSenderFilePreviewedEventProps,
  type LetterSenderFileRemovedEventProps,
  type LetterSenderFileUploadedEventProps,
  type LetterSenderModalClosedEventProps,
  type LetterSenderModalOpenedEventProps,
  type LetterSenderProviderSearchedEventProps,
  type LetterSenderRecipientAddedEventProps,
  type LetterSenderRecipientRemovedEventProps,
  type LetterSenderSendFailedEventProps,
  type LetterSenderSentEventProps,
} from '@/models/analytics/features/letter-sender';
import {
  type ReplacementsUploadedEventProps,
  type SnippetsUploadedEventProps,
} from '@/models/analytics/features/memory';
import {
  type MiniModeClosedEventProps,
  type MiniModeControlAttemptedEventProps,
  type MiniModeControlClickedEventProps,
  type MiniModeNotepadEditedEventProps,
  type MiniModeOpenedEventProps,
  type MiniModePreferenceChangedEventProps,
} from '@/models/analytics/features/mini-mode';
import {
  type NoteAutoRetryDecidedEventProps,
  type NoteCopiedEventProps,
  type NoteEditorRenderedEventProps,
  type NoteEditStartedEventProps,
  type NoteFailedEventProps,
  type NoteFailureOverlayClearedEventProps,
  type NoteFailurePresentedEventProps,
  type NoteGenerationCompletedEventProps,
  type NoteGenerationFailedEventProps,
  type NoteGenerationStartedEventProps,
  type NoteInsertAppliedEventProps,
  type NotePushFailedEventProps,
  type NoteSectionCreatedEventProps,
  type NoteSectionDeletedEventProps,
  type NoteStartedEventProps,
} from '@/models/analytics/features/notes';
import { type NudgeProtocolsToggledEventProps } from '@/models/analytics/features/nudge-protocols';
import {
  type OfflineRecordingDeletedEventProps,
  type OfflineRecordingQueuedEventProps,
  type OfflineRecordingStartedEventProps,
  type OfflineSessionDeleteEnqueueFailedEventProps,
  type OfflineSessionDeleteQueuedEventProps,
  type OfflineSessionDeleteReplayFailedEventProps,
  type OfflineSyncCompletedEventProps,
  type OfflineSyncDeferredEventProps,
  type OfflineSyncFailedEventProps,
} from '@/models/analytics/features/offline-recording';
import {
  type OnboardingAuthenticationCompletedEventProps,
  type OnboardingCompletedEventProps,
  type OnboardingDocumentCreatedEventProps,
  type OnboardingDocumentsSelectedEventProps,
  type OnboardingEvidenceAcceptedEventProps,
  type OnboardingEvidenceDiscardedEventProps,
  type OnboardingEvidenceQueriedEventProps,
  type OnboardingLearnBasicsCompletedEventProps,
  type OnboardingLearnBasicsSkippedEventProps,
  type OnboardingNoteStyleUpdatedEventProps,
  type OnboardingStartedEventProps,
  type OnboardingStepCompletedEventProps,
  type OnboardingStepSkippedEventProps,
  type OnboardingTemplateCompletedEventProps,
  type OnboardingTemplateReviewedEventProps,
  type OnboardingTemplateStartedEventProps,
  type OnboardingUpdatedEventProps,
  type OrganisationSurveySubmittedEventProps,
  type SignupPhoneFieldViewedEventProps,
} from '@/models/analytics/features/onboarding';
import {
  type AutoLinkSessionsPreferenceChangedEventProps,
  type ContextLinkedEventProps,
  type ContextPreviewedEventProps,
  type ContextUnlinkedEventProps,
  type LinkedSessionsSpotlightDismissedEventProps,
  type PatientAutoExtractPreferenceChangedEventProps,
  type PatientLinkingTimeframeDaysChangedEventProps,
  type PatientLinkingTimeframeSelectedEventProps,
  type PatientLinkingToggledEventProps,
} from '@/models/analytics/features/past-context';
import {
  type PatientContextToggleOffEventProps,
  type PatientContextToggleOnEventProps,
  type PatientCreatedEventProps,
  type PatientDeleteBlockedEventProps,
  type PatientDeleteClickedEventProps,
  type PatientDirectoryEnableClickedEventProps,
  type PatientDocumentLinkedEventProps,
  type PatientDocumentUnlinkedEventProps,
  type PatientDocumentUploadedEventProps,
  type PatientExtractionModalOpenedEventProps,
  type PatientExtractionReviewedEventProps,
  type PatientFieldEditedEventProps,
  type PatientLinkedEventProps,
  type PatientListViewedEventProps,
  type PatientProfileOpenedEventProps,
  type PatientProfileTabViewedEventProps,
  type PatientRecapPopoverOpenedEventProps,
  type PatientRecapSourceSessionClickedEventProps,
  type PatientSelectedEventProps,
  type PatientSessionExpandedEventProps,
  type PatientSummaryCtaShownEventProps,
  type PatientSummaryEmailSendAttemptedEventProps,
  type PatientSummaryEmailSentEventProps,
  type PatientSummarySmsSendAttemptedEventProps,
  type PatientSummarySmsSentEventProps,
  type PatientUnlinkedEventProps,
  type PatientUpdatedEventProps,
  type PatientViewedEventProps,
} from '@/models/analytics/features/patient';
import {
  type ImproveNotesTaskCompletedEventProps,
  type NoteComparisonViewedEventProps,
  type PostNoteExperienceCompletedEventProps,
  type SpotlightAdvancedEventProps,
  type SpotlightClosedEventProps,
  type TrialDaysUnlockedEventProps,
} from '@/models/analytics/features/post-note-quality';
import {
  type PrechartSentToContextEventProps,
  type PrechartViewedEventProps,
} from '@/models/analytics/features/prechart';
import { type ProductTourViewedEventProps } from '@/models/analytics/features/product-tour';
import {
  type ReferralCompletedEventProps,
  type ReferralCreatedEventProps,
  type ReferralSharedEventProps,
  type ReferralViewedEventProps,
} from '@/models/analytics/features/referral';
import {
  type SettingsModalClosedEventProps,
  type SettingsModalOpenedEventProps,
  type SettingsModalSectionChangedEventProps,
  type SettingsModalUnknownRouteEventProps,
  type SettingsUpdatedEventProps,
} from '@/models/analytics/features/settings';
import {
  type SnippetCreatedEventProps,
  type SnippetDeletedEventProps,
  type SnippetUpdatedEventProps,
  type SnippetUploadedEventProps,
} from '@/models/analytics/features/snippets';
import {
  type SourceRecommendationAppliedEventProps,
  type SourceRecommendationCustomisedEventProps,
  type SourceRecommendationDismissedEventProps,
  type SourceRecommendationShownEventProps,
} from '@/models/analytics/features/source-recommendation';
import {
  type ScribeStyleUpdatedEventProps,
  type VoiceStyleUpdatedEventProps,
} from '@/models/analytics/features/styles';
import {
  type SurveySubmittedEventProps,
  type SurveyUpdatedEventProps,
  type SurveyViewedEventProps,
} from '@/models/analytics/features/survey';
import {
  type TaskCompletedEventProps,
  type TaskCreatedEventProps,
  type TaskDelegatedEventProps,
  type TaskDeletedEventProps,
  type TaskListViewedEventProps,
  type TaskNotificationClickedEventProps,
  type TaskUpdatedEventProps,
  type TaskViewedEventProps,
} from '@/models/analytics/features/tasks';
import {
  type TeamCreatedEventProps,
  type TeamEvidenceAccessUpdatedEventProps,
  type TeamHeidiIiAccessUpdatedEventProps,
  type TeamMemberAcceptedEventProps,
  type TeamMemberCreatedEventProps,
  type TeamMemberDeletedEventProps,
  type TeamUpdatedEventProps,
} from '@/models/analytics/features/team';
import {
  type TemplateCompletedEventProps,
  type TemplateConvertedProps,
  type TemplateCreatedProps,
  type TemplateCreationRecordCreatedProps,
  type TemplateDeletedEventProps,
  type TemplateEditorUpdatedProps,
  type TemplateFailedEventProps,
  type TemplateGenerationProps,
  type TemplateOpenedProps,
  type TemplateQualityGateEventProps,
  type TemplateRevertedEventProps,
  type TemplateSearchedEventProps,
  type TemplateUpdatedProps,
  type TemplateViewedEventProps,
} from '@/models/analytics/features/templates';
import {
  type AccountDeletedEventProps,
  type ApplicationInstalledEventProps,
  type AuthenticationClickedEventProps,
  type AuthManualControlEventProps,
  type HelpViewedEventProps,
  type SignOutEventProps,
  type SignupConversionEventProps,
  type UserClickedEventProps,
  type UserSignedInEventProps,
  type UserSignedUpEventProps,
  type UserStartedEventProps,
  type UserUpdatedEventProps,
} from '@/models/analytics/features/user';
import type {
  LibraryCollectionCreatedEventProps,
  LibraryDocumentAddedToCollectionEventProps,
  LibraryDocumentScopeChangedEventProps,
  LibraryDocumentUploadedEventProps,
  LibraryLinkClickedEventProps,
  LibraryUploadNudgeClickedEventProps,
} from '@heidi/evidence/contracts';
import { type ExperimentProps } from '@heidi/session/contracts';
import { type FormTemplateFieldsUpdatedEventProps } from '@heidi/templates';
import { type BookDemoFormSubmittedEventProps } from './features/book-demo';
import {
  type DeviceEventProps,
  type HardwareBleReconnectedEventProps,
  type HardwareBluetoothPermissionResultEventProps,
  type HardwareBluetoothSettingsOpenedEventProps,
  type HardwareBulkSyncCancelledEventProps,
  type HardwareBulkSyncCompletedEventProps,
  type HardwareBulkSyncDbMigrationAppliedEventProps,
  type HardwareBulkSyncDbMigrationFailedEventProps,
  type HardwareBulkSyncFailedEventProps,
  type HardwareBulkSyncFileCrcCheckedEventProps,
  type HardwareBulkSyncFileFailedEventProps,
  type HardwareBulkSyncFileRetryEventProps,
  type HardwareBulkSyncPhaseChangedEventProps,
  type HardwareBulkSyncResumedEventProps,
  type HardwareBulkSyncStartedEventProps,
  type HardwareBulkSyncTaskFailedEventProps,
  type HardwareBulkSyncTriggeredEventProps,
  type HardwareContactSupportTappedEventProps,
  type HardwareDetailViewedEventProps,
  type HardwareDeviceAlreadyPairedEventProps,
  type HardwareDeviceConnectedEventProps,
  type HardwareDeviceConnectionFailedEventProps,
  type HardwareDeviceDisconnectedEventProps,
  type HardwareDeviceLinkedEventProps,
  type HardwareDeviceLinkFailedEventProps,
  type HardwareDeviceNotFoundEventProps,
  type HardwareDeviceReconnectAttemptEventProps,
  type HardwareDeviceReconnectTimeoutEventProps,
  type HardwareDeviceRecordingStoppedEventProps,
  type HardwareDeviceRemovedEventProps,
  type HardwareDeviceRemoveFailedEventProps,
  type HardwareDeviceRemoveTappedEventProps,
  type HardwareDeviceScanStartedEventProps,
  type HardwareErrorRecoveryActionTakenEventProps,
  type HardwareErrorShownEventProps,
  type HardwareFileDecryptionFailedEventProps,
  type HardwareFileDecryptionSkippedEventProps,
  type HardwareFileDecryptionSuccessEventProps,
  type HardwareInputModeSelectedEventProps,
  type HardwareLearnMoreTappedEventProps,
  type HardwareLowBatteryShownEventProps,
  type HardwareRecordingChunkUploadFailedEventProps,
  type HardwareRecordingFirstChunkUploadedEventProps,
  type HardwareRecordingInterruptedEventProps,
  type HardwareRecordingPauseEventProps,
  type HardwareRecordingPauseFailedEventProps,
  type HardwareRecordingRecoveredEventProps,
  type HardwareRecordingResumeEventProps,
  type HardwareRecordingResumeFailedEventProps,
  type HardwareRecordingStartBlockedEventProps,
  type HardwareRecordingStartEventProps,
  type HardwareRecordingStartFailedEventProps,
  type HardwareRecordingStopEventProps,
  type HardwareRecordingStopFailedEventProps,
  type HardwareRecoveryCompletedEventProps,
  type HardwareRecoveryFailedEventProps,
  type HardwareRetryConnectionTappedEventProps,
  type HardwareSessionCreatedEventProps,
  type HardwareSetupCompletedEventProps,
  type HardwareSetupStartedEventProps,
  type HardwareSetupStepViewedEventProps,
  type HardwareTransportFallbackEventProps,
  type HardwareUsbAccessDeniedEventProps,
  type HardwareUsbModeToggledEventProps,
  type HardwareUsbModeToggleFailedEventProps,
  type HardwareWifiSetupCompletedEventProps,
  type HardwareWifiSetupFailedEventProps,
  type HardwareWifiSetupStartedEventProps,
  type HardwareWifiTransferRequestedEventProps,
  type NativeFirmwareApplyCompleteEventProps,
  type NativeFirmwareApplyFailedEventProps,
  type NativeFirmwareApplyStartEventProps,
  type NativeFirmwareBatteryCheckFailedEventProps,
  type NativeFirmwareDownloadCompleteEventProps,
  type NativeFirmwareDownloadFailedEventProps,
  type NativeFirmwareDownloadStartEventProps,
  type NativeFirmwarePostUpdateReconnectedEventProps,
  type NativeFirmwareUpdateAvailableEventProps,
  type NativeFirmwareUpdateCheckFailedEventProps,
  type NativeFirmwareUpdateCheckStartEventProps,
  type NativeFirmwareUpdateTappedEventProps,
  type NativeFirmwareUpgradeCompleteEventProps,
  type NativeFirmwareUpgradeFailedEventProps,
  type NativeFirmwareUpgradeStartEventProps,
  type NativeFirmwareUploadCompleteEventProps,
  type NativeFirmwareUploadFailedEventProps,
  type NativeFirmwareUploadStartEventProps,
  type NativeFirmwareUpToDateEventProps,
} from './features/chronicle';
import {
  type CoachMarkEventProps,
  type DemoCompletedEventProps,
  type FirstDocumentCreatedEventProps,
} from './features/demo-flow';
import {
  type ExportContentSavedEventProps,
  type ExportSettingToggledEventProps,
} from './features/export-settings';
import { type FeedbackSubmittedEventProps } from './features/feedback';
import {
  type RemoteIntroPromoClickedEventProps,
  type RemoteIntroPromoDismissedEventProps,
  type RemoteIntroPromoViewedEventProps,
} from './features/growth-remote-promo';
import {
  type EhrStartedEventProps,
  type GdtFileParsedEventProps,
  type HaikuTabChangedEventProps,
  type IntegrationCompletedEventProps,
  type IntegrationCreatedEventProps,
  type IntegrationDataCompletedEventProps,
  type IntegrationDataFailedEventProps,
  type IntegrationDataRetrievedEventProps,
  type IntegrationFailedEventProps,
  type IntegrationLinkedEventProps,
  type IntegrationNoteSyncedEventProps,
  type IntegrationPatientSelectedEventProps,
  type IntegrationSessionCreatedEventProps,
  type IntegrationStartedEventProps,
  type IntegrationSubmittedEventProps,
  type IntegrationUnlinkedEventProps,
  type IntegrationUpdatedEventProps,
  type IntegrationUserSearchedEventProps,
  type IntegrationWidgetAuthStepErroredEventProps,
  type IntegrationWidgetAuthStepMiscEventProps,
  type PatientSearchedEventProps,
  type PushWarningResolvedEventProps,
  type PushWarningViewedEventProps,
  type SectionLinkedEventProps,
  type TemplateSelectedEventProps,
  type TokenGeneratedEventProps,
  type TranscriptionSkippedEventProps,
  type TranscriptionStartedEventProps,
  type TranscriptionStoppedEventProps,
} from './features/integrations';
import {
  type CountrySelectedEventProps,
  type LanguageSelectedEventProps,
} from './features/locale-selection';
import {
  type NoteContentPastedEventProps,
  type NoteCreatedEventProps,
  type NoteDisplayedEventProps,
  type NoteRevertedEventProps,
  type NoteSelectedEventProps,
  type NoteSelectionSkippedEventProps,
  type NoteUpdatedEventProps,
} from './features/notes';
import {
  type EarlyMeetingChoiceSelectedEventProps,
  type EarlyMeetingRegionNotSupportedEventProps,
  type MeetingBookedEventProps,
  type MeetingBookingFailedEventProps,
  type MeetingBookingSubmittedEventProps,
  type MeetingFallbackToRequestDemoEventProps,
  type MeetingSlotSelectedEventProps,
  type OnboardingDemoVariantViewedEventProps,
  type PhoneDemoBookedEventProps,
  type SdrDemoOptInChangedEventProps,
  type SdrDemoRequestedEventProps,
  type WebinarOptInChangedEventProps,
  type WebinarRegistrationSubmittedEventProps,
  type WebinarTimeslotSelectedEventProps,
} from './features/onboarding-hubspot';
import {
  type CancelFlowOpenedEventProps,
  type CancelOfferEventProps,
  type CancelReasonSelectedEventProps,
  type CheckoutStartedEventProps,
  type PaymentEventProps,
  type PaywallEventProps,
} from './features/payments';
import { type SessionLayoutSwitchedEventProps } from './features/session-layout';
import {
  type SessionMergeConfirmedEventProps,
  type SessionMergeDissolveCancelledEventProps,
  type SessionMergeDissolveConfirmedEventProps,
  type SessionMergeDissolvePromptViewedEventProps,
  type SessionMergeFailedEventProps,
  type SessionMergeIgnoredEventProps,
  type SessionMergeOnboardingAcceptedEventProps,
  type SessionMergeOnboardingDismissedEventProps,
  type SessionMergeOnboardingViewedEventProps,
  type SessionMergeRelinkedEventProps,
  type SessionMergeReviewDismissedEventProps,
  type SessionMergeReviewViewedEventProps,
  type SessionMergeTabViewedEventProps,
  type SessionMergeUnlinkCancelledEventProps,
  type SessionMergeUnlinkConfirmedEventProps,
  type SessionMergeUnlinkedEventProps,
  type SessionMergeUnlinkPromptViewedEventProps,
} from './features/session-merge';
import {
  type SessionLinkShareOtpFailedEventProps,
  type SessionLinkShareOtpVerifiedEventProps,
  type ShareAccessChangedEventProps,
  type ShareAccessRevokedEventProps,
  type ShareButtonClickedEventProps,
  type ShareEditRequestedEventProps,
  type ShareExternalConfirmedEventProps,
  type ShareExternalEmailAddedEventProps,
  type ShareExternalEmailRemovedEventProps,
  type ShareExternalInlineCancelledEventProps,
  type ShareExternalInlineOpenedEventProps,
  type ShareExternalLinkSendSubmittedEventProps,
  type ShareExternalRevealOpenedEventProps,
  type ShareLinkCopiedEventProps,
  type ShareLinkGeneratedEventProps,
  type ShareMemberAddedEventProps,
  type ShareMemberSearchedEventProps,
  type ShareModalOpenedEventProps,
  type ShareNotificationClickedEventProps,
  type ShareNotificationReceivedEventProps,
  type ShareSavedEventProps,
} from './features/session-sharing';
import {
  type ActiveSessionControlClickedEventProps,
  type ActiveSessionIndicatorClickedEventProps,
  type ActiveSessionNavigationEventProps,
  type CopySettingsUpdatedEventProps,
  type PersonalizationUpdatedEventProps,
  type ScheduleSyncedEventProps,
  type ScheduleUploadCompletedEventProps,
  type ScheduleUploadProcessingFailedEventProps,
  type ScheduleUploadPromoClickedEventProps,
  type ScheduleUploadPromoViewedEventProps,
  type ScheduleUploadSessionsCreatedEventProps,
  type ScheduleUploadStartedEventProps,
  type ScheduleViewedEventProps,
  type SessionCopiedEventProps,
  type SessionCreatedEventProps,
  type SessionDeletedEventProps,
  type SessionDetailResumeStartedEventProps,
  type SessionMarkedAsContextOnlyEventProps,
  type SessionSearchUsageBaselineEventProps,
  type SessionsFilterAppliedEventProps,
  type SessionsListPaginatedEventProps,
  type SessionsScopeChangedEventProps,
  type SessionsSortChangedEventProps,
  type SessionTabSwitchedEventProps,
  type SessionTabViewedEventProps,
  type SessionUpdatedEventProps,
  type SessionViewedEventProps,
  type TranscriptDeletedEventProps,
} from './features/sessions';
import {
  type SubscriptionCreatedEventProps,
  type TrialCompletedEventProps,
  type TrialEventProps,
} from './features/trial';
import {
  type ButtonEventProps,
  type FieldSelectedEventProps,
  type ModalDismissedEventProps,
  type ModalEventProps,
  type ScreenEventProps,
  type UiInteractionEventProps,
} from './features/ui';

// ——— Event Names & Discriminated Union ——————————————————————————————————————————————
// Going forwards, all event names should be in snake_case
export enum TrackingEventName {
  // UI
  UiInteraction = 'ui_interaction',
  ButtonClicked = 'button_clicked',
  ScreenViewed = 'screen_viewed',
  ModalViewed = 'modal_viewed',
  ModalDismissed = 'modal_dismissed',
  FieldSelected = 'field_selected',
  PanelOpened = 'panel_opened',
  PanelClosed = 'panel_closed',
  ToggleUpdated = 'toggle_updated',

  // Chat (object_action pattern for Evidence)
  MessageSubmitted = 'message_submitted',
  MessageReceived = 'message_received',
  MessageFailed = 'message_failed',
  MessageAborted = 'message_aborted',
  ChatCreated = 'chat_created',
  ChatViewed = 'chat_viewed',
  ChatDeleted = 'chat_deleted',

  // Notes
  NoteCreated = 'note_created',
  NoteSelected = 'note_selected',
  NoteDisplayed = 'note_displayed',
  NoteSelectionSkipped = 'note_selection_skipped',
  NoteUpdated = 'note_updated',
  NoteReverted = 'note_reverted',
  NoteGenerationStarted = 'note_generation_started',
  NoteGenerationCompleted = 'note_generation_completed',
  NoteGenerationFailed = 'note_generation_failed',
  NoteContentPasted = 'note_content_pasted',
  NoteInsertApplied = 'note_insert_applied',

  // Post-note quality ("Improve your notes") — GRO-3284
  ImproveNotesTaskCompleted = 'improve_notes_task_completed',
  PostNoteExperienceCompleted = 'post_note_experience_completed',
  NoteComparisonViewed = 'note_comparison_viewed',
  TrialDaysUnlocked = 'trial_days_unlocked',
  SpotlightAdvanced = 'spotlight_advanced',
  SpotlightClosed = 'spotlight_closed',

  // Onboarding
  OnboardingStarted = 'onboarding_started',
  OnboardingStepCompleted = 'onboarding_step_completed',
  OnboardingStepSkipped = 'onboarding_step_skipped',
  OnboardingCompleted = 'onboarding_completed',
  OnboardingErrorViewed = 'onboarding_error_viewed',
  OnboardingTemplateStarted = 'onboarding_template_started',
  OnboardingTemplateCompleted = 'onboarding_template_completed',
  OnboardingTemplateReviewed = 'onboarding_template_reviewed',
  OnboardingDocumentsSelected = 'onboarding_documents_selected',
  OnboardingNoteStyleUpdated = 'onboarding_note_style_updated',
  OnboardingEvidenceQueried = 'onboarding_evidence_queried',
  OnboardingEvidenceAccepted = 'onboarding_evidence_accepted',
  OnboardingEvidenceDiscarded = 'onboarding_evidence_discarded',
  OnboardingDocumentCreated = 'onboarding_document_created',
  OnboardingLearnBasicsCompleted = 'onboarding_learn_basics_completed',
  OnboardingLearnBasicsSkipped = 'onboarding_learn_basics_skipped',
  SignupPhoneFieldViewed = 'signup_phone_field_viewed',
  AuthenticationCompleted = 'authentication_completed',
  ForcedLogout = 'forced_logout',
  OrganisationSurveySubmitted = 'organisation_survey_submitted',

  // Account
  AccountCreated = 'account_created',

  // Session
  SessionCreated = 'session_created',
  SessionViewed = 'session_viewed',
  SessionCopied = 'session_copied',
  CopySettingsUpdated = 'copy_settings_updated',
  SessionSearchUsageBaseline = 'session_search_usage_baseline',
  SessionsFilterApplied = 'sessions_filter_applied',
  SessionsScopeChanged = 'sessions_scope_changed',
  SessionsSortChanged = 'sessions_sort_changed',
  SessionsListPaginated = 'sessions_list_paginated',
  SessionUpdated = 'session_updated',
  SessionMarkedAsContextOnly = 'session_marked_as_context_only',
  /** A user switched the Scribe session layout between V1 and V2. */
  SessionLayoutSwitched = 'session_layout_switched',
  /** Assistant action from the session panel (create document, export, copy). */
  AssistantPanelAction = 'assistant_panel_action',

  // Offline desktop recording (Snowplow-first; emit with options: { amplitude: false })
  OfflineRecordingStarted = 'offline_recording_started',
  OfflineRecordingQueued = 'offline_recording_queued',
  OfflineRecordingDeleted = 'offline_recording_deleted',
  OfflineSyncCompleted = 'offline_sync_completed',
  OfflineSyncFailed = 'offline_sync_failed',
  OfflineSyncDeferred = 'offline_sync_deferred',
  // Offline queued session delete. A distinct object from the events above:
  // those describe a LOCAL recording's capture and sync, these a SERVER
  // session the clinician deleted while offline. Counts and opaque error
  // codes only — never a session id or title.
  OfflineSessionDeleteQueued = 'offline_session_delete_queued',
  OfflineSessionDeleteEnqueueFailed = 'offline_session_delete_enqueue_failed',
  OfflineSessionDeleteReplayFailed = 'offline_session_delete_replay_failed',

  // Schedule
  ScheduleViewed = 'schedule_viewed',
  ScheduleSynced = 'schedule_synced',
  ScheduleUploadStarted = 'schedule_upload_started',
  ScheduleUploadCompleted = 'schedule_upload_completed',
  ScheduleUploadProcessingFailed = 'schedule_upload_processing_failed',
  ScheduleUploadSessionsCreated = 'schedule_upload_sessions_created',
  ScheduleUploadPromoViewed = 'schedule_upload_promo_viewed',
  ScheduleUploadPromoClicked = 'schedule_upload_promo_clicked',

  // Heidi Remote intro promo (GRO-2981)
  RemoteIntroPromoViewed = 'remote_intro_promo_viewed',
  RemoteIntroPromoClicked = 'remote_intro_promo_clicked',
  RemoteIntroPromoDismissed = 'remote_intro_promo_dismissed',

  // Experiments
  NoteCopied = 'note_copied',

  // Transcription
  TranscriptionStarted = 'transcription_started',
  TranscriptionStopped = 'transcription_stopped',
  /** GRO-3186: user clicked "Show the results" in the shortened-demo flow,
   *  bypassing the recording phase entirely. */
  TranscriptionSkipped = 'transcription_skipped',

  // Live Transcription Indicator (Record Anywhere)
  ActiveSessionIndicatorClicked = 'active_session_indicator_clicked',
  ActiveSessionControlClicked = 'active_session_control_clicked',
  ActiveSessionNavigation = 'active_session_navigation',

  // Dictation
  DictationStarted = 'universal_dictation_started',
  DictationCompleted = 'universal_dictation_completed',
  DictationErrored = 'universal_dictation_errored',
  LegacyInlineDictationCompleted = 'legacy_inline_dictation_completed',

  // Dictation CSAT
  DictationCsatSubmitted = 'universal_dictation_csat_submitted',
  DictationCsatDismissed = 'universal_dictation_csat_dismissed',

  // Experiments
  ExperimentStarted = 'experiment_started',

  // Monetization
  PaywallViewed = 'paywall_viewed',
  CheckoutStarted = 'checkout_started',
  CancelFlowOpened = 'cancel_flow_opened',
  CancelReasonSelected = 'cancel_reason_selected',
  CancelOfferShown = 'cancel_offer_shown',
  CancelOfferAccepted = 'cancel_offer_accepted',
  CancelOfferDeclined = 'cancel_offer_declined',
  PaymentStarted = 'payment_started',
  PaymentCompleted = 'payment_completed',
  TrialStarted = 'trial_started',
  TrialCompleted = 'trial_completed',
  SubscriptionCreated = 'subscription_created',

  // Compliance
  ComplianceCheck = 'compliance_check',

  // Auth
  UserSignedIn = 'user_signed_in',
  UserSignedUp = 'user_signed_up',
  /**
   * LOAD-BEARING marketing event — the browser-side half of the signup
   * conversion pair (server-side half is `sign_up_successful`), consumed by Meta
   * for ad optimization. Invariants MUST hold (guarded by a Datadog monitor
   * + `analyticsScribeModel.signupConversion.test.ts`):
   *   1. fires exactly once per legitimate signup, and
   *   2. flag-gated by `op-signup-conversion-event` — the single control
   *      point for rollout/kill-switch AND environment scoping (enabled only in
   *      the production Flagsmith environment, so staging/dev never reach Meta).
   * Carries the full browser-side attribution set (UTMs + ad click IDs +
   * landing URL). Fired from `analyticsScribeModel.trackSignUpSuccessfulWebAuth`
   * alongside `user_signed_up`. Do not repurpose or add call sites without
   * preserving the invariants.
   */
  SignupConversion = 'signup_conversion',
  UserSignedOut = 'user_signed_out',
  AuthManualLinkCopied = 'auth_manual_link_copied',
  AuthManualOpenClicked = 'auth_manual_open_clicked',
  AuthFallbackUsed = 'auth_fallback_used',

  // Feedback
  FeedbackSubmitted = 'feedback_submitted',

  // Force Update
  ForceUpdateViewed = 'force_update_viewed',
  ForceUpdateDeferred = 'force_update_deferred',
  ForceUpdateInitiated = 'force_update_initiated',
  // Documents
  DocumentExported = 'document_exported',
  /** @deprecated Use DocumentExported with format: 'print' */
  FormFillingDownloaded = 'form_filling_downloaded',
  DocumentCreated = 'document_created',
  DocumentGenerated = 'document_generated',
  DocumentUpdated = 'document_updated',

  // Queued document templates
  DocumentTemplateQueueLimitReached = 'document_template_queue_limit_reached',
  DocumentTemplateQueueReordered = 'document_template_queue_reordered',
  SessionStartedWithDocumentQueue = 'session_started_with_document_queue',
  QueuedDocumentGenerationStarted = 'queued_document_generation_started',
  QueuedDocumentGenerationFailed = 'queued_document_generation_failed',
  QueuedDocumentGenerationCancelled = 'queued_document_generation_cancelled',
  QueuedDocumentTabTemplateSwapped = 'queued_document_tab_template_swapped',

  // Memory (Snippets & Replacements)
  SnippetsUploaded = 'snippets_uploaded',
  ReplacementsUploaded = 'replacements_uploaded',

  // Style Updates
  ScribeStyleUpdated = 'scribe_style_updated',
  VoiceStyleUpdated = 'voice_style_updated',
  AmbientHeidiTriggered = 'ambient_heidi_triggered',

  // Clinical Codes
  ClinicalCodeCopied = 'clinical_code_copied',
  ClinicalCodeClickable = 'clinical_code_clickable',

  // Clinical Calculators
  CalculatorExecuted = 'calculator_executed',

  // Patient
  ExtractionModalOpened = 'patient_extraction_modal_opened',
  ExtractionReviewed = 'patient_extraction_reviewed',
  PatientSummarySmsSendAttempted = 'patient_summary_sms_send_attempted',
  PatientSummarySmsSent = 'patient_summary_sms_sent',
  PatientSummaryEmailSendAttempted = 'patient_summary_email_send_attempted',
  PatientSummaryEmailSent = 'patient_summary_email_sent',
  PatientSummaryCtaShown = 'patient_summary_cta_shown',
  PatientViewed = 'patient_viewed',
  PatientUpdated = 'patient_updated',
  PatientCreated = 'patient_created',
  PatientLinked = 'patient_linked',
  PatientSelected = 'patient_selected',
  PatientUnlinked = 'patient_unlinked',
  PatientContextToggleOn = 'patient_context_toggle_on',
  PatientContextToggleOff = 'patient_context_toggle_off',
  PatientRecapSourceSessionClicked = 'patient_recap_source_session_clicked',

  // Session Merge (Snowplow-first; emit with options: { amplitude: false })
  SessionMergeReviewViewed = 'session_merge_review_viewed',
  SessionMergeConfirmed = 'session_merge_confirmed',
  SessionMergeIgnored = 'session_merge_ignored',
  SessionMergeReviewDismissed = 'session_merge_review_dismissed',
  SessionMergeFailed = 'session_merge_failed',
  SessionMergeTabViewed = 'session_merge_tab_viewed',
  SessionMergeUnlinked = 'session_merge_unlinked',
  SessionMergeRelinked = 'session_merge_relinked',
  SessionMergeDissolvePromptViewed = 'session_merge_end_prompt_viewed',
  SessionMergeDissolveConfirmed = 'session_merge_end_confirmed',
  SessionMergeDissolveCancelled = 'session_merge_end_cancelled',
  SessionMergeUnlinkPromptViewed = 'session_merge_unlink_prompt_viewed',
  SessionMergeUnlinkConfirmed = 'session_merge_unlink_confirmed',
  SessionMergeUnlinkCancelled = 'session_merge_unlink_cancelled',
  SessionMergeOnboardingViewed = 'session_merge_onboarding_viewed',
  SessionMergeOnboardingAccepted = 'session_merge_onboarding_accepted',
  SessionMergeOnboardingDismissed = 'session_merge_onboarding_dismissed',

  // Patient surface (list / drawer / full-page profile)
  PatientListViewed = 'patient_list_viewed',
  PatientProfileOpened = 'patient_profile_opened',
  PatientProfileTabViewed = 'patient_profile_tab_viewed',
  PatientSessionExpanded = 'patient_session_expanded',
  PatientFieldEdited = 'patient_field_edited',
  PatientDocumentUploaded = 'patient_document_uploaded',
  PatientDocumentLinked = 'patient_document_linked',
  PatientDocumentUnlinked = 'patient_document_unlinked',
  PatientRecapPopoverOpened = 'patient_recap_popover_opened',

  // Shared patient directory dialogs (Snowplow-first; emit with options: { amplitude: false })
  PatientDirectoryEnableClicked = 'patient_directory_enable_clicked',
  PatientDeleteClicked = 'patient_delete_clicked',
  PatientDeleteBlocked = 'patient_delete_blocked',

  // Pre-chart (Snowplow-first; emit with options: { amplitude: false })
  PrechartViewed = 'prechart_viewed',
  PrechartSentToContext = 'prechart_sent_to_context',

  // Tab sharing (Telehealth)
  TabSharingDoNoShowMessageAgain = 'tab_sharing_do_show_message_again',
  TabSharingNotAvailableModalShown = 'tab_sharing_not_available_modal_shown',

  // Templates
  TemplateCreated = 'template_created',
  TemplateCreationRecordCreated = 'template_creation_record_created',
  TemplateUpdated = 'template_updated',
  TemplateEditorUpdated = 'template_editor_updated',
  TemplateGeneration = 'template_generation',
  TemplateOpened = 'template_opened',
  TemplateReverted = 'template_reverted',

  // Book Demo
  BookDemoFormSubmitted = 'book_demo_form_submitted',

  // Book Demo
  BookDemoCountrySelected = 'book_demo_country_selected',
  BookDemoCliniciansSelected = 'book_demo_clinicians_selected',
  BookDemoNextClicked = 'book_demo_next_clicked',

  // Onboarding meeting / webinar (HubSpot)
  MeetingSlotSelected = 'meeting_slot_selected',
  MeetingBookingSubmitted = 'meeting_booking_submitted',
  MeetingBooked = 'meeting_booked',
  MeetingBookingFailed = 'meeting_booking_failed',
  MeetingFallbackToRequestDemo = 'meeting_fallback_to_request_demo',
  WebinarOptInChanged = 'webinar_opt_in_changed',
  WebinarTimeslotSelected = 'webinar_timeslot_selected',
  WebinarRegistrationSubmitted = 'webinar_registration_submitted',
  SdrDemoOptInChanged = 'sdr_demo_opt_in_changed',
  SdrDemoRequested = 'sdr_demo_requested',
  PhoneDemoBooked = 'phone_demo_booked',
  EarlyMeetingChoiceSelected = 'early_meeting_choice_selected',
  EarlyMeetingRegionNotSupported = 'early_meeting_region_not_supported',
  OnboardingDemoVariantViewed = 'onboarding_demo_variant_viewed',

  // Locale selection
  CountrySelected = 'country_selected',
  LanguageSelected = 'language_selected',

  // Context Files
  ContextFileUploadStarted = 'context_file_upload_started',
  ContextFileUploaded = 'context_file_uploaded',
  ContextFileUploadFailed = 'context_file_upload_failed',
  ContextFileDeleted = 'context_file_deleted',

  // Context (Previous Notes/Sessions linked to current session)
  ContextPreviewed = 'context_previewed',
  ContextLinked = 'context_linked',
  ContextUnlinked = 'context_unlinked',
  AutoLinkSessionsPreferenceChanged = 'auto_link_sessions_preference_changed',
  PatientAutoExtractPreferenceChanged = 'patient_auto_extract_preference_changed',
  LinkedSessionsSpotlightDismissed = 'linked_sessions_spotlight_dismissed',
  // Dictation Logs awareness spotlight
  DictationLogsSpotlightDismissed = 'dictation_logs_spotlight_dismissed',
  // Dictate-page onboarding tour entry banner (SCR-7070)
  DictateTourBannerCtaClicked = 'dictate_tour_banner_cta_clicked',
  // Session Linking settings
  PatientLinkingToggled = 'patient_linking_toggled',
  PatientLinkingTimeframeSelected = 'patient_linking_timeframe_selected',
  PatientLinkingTimeframeDaysChanged = 'patient_linking_timeframe_days_changed',

  // Evidence
  EvidenceCategoryClicked = 'evidence_category_clicked',
  EvidenceVerbClicked = 'evidence_verb_clicked',
  EvidenceSuggestionClicked = 'evidence_suggestion_clicked',
  EvidenceSignUpWallViewed = 'evidence_signup_wall_viewed',
  EvidenceSourceClicked = 'evidence_source_clicked',
  EvidenceRequestAccessClicked = 'evidence_request_access_clicked',
  EvidenceContactSalesClicked = 'evidence_contact_sales_clicked',
  EvidenceNhsContactSalesSubmitted = 'evidence_nhs_contact_sales_submitted',
  EvidenceResponseFeedbackSubmitted = 'evidence_response_feedback_submitted',
  EvidenceReferenceFeedbackSubmitted = 'evidence_reference_feedback_submitted',
  EvidenceReferenceScrollClicked = 'evidence_reference_scroll_clicked',
  EvidenceReferencesSectionToggled = 'evidence_references_section_toggled',
  EvidenceShareModalOpened = 'evidence_share_modal_opened',
  EvidenceShareLinkCopied = 'evidence_share_link_copied',
  EvidenceShareSocialClicked = 'evidence_share_social_clicked',
  EvidenceSharedChatOpened = 'evidence_shared_chat_opened',
  EvidenceCopyConfirmation = 'evidence_copy_confirmation',
  EvidenceCopied = 'evidence_copied',
  EvidenceCheckOtherSourcesClicked = 'evidence_check_other_sources_clicked',
  FollowUpQuestionClicked = 'follow_up_question_clicked',
  // Evidence - Chat events
  /** @deprecated Use MessageSubmitted with source: 'evidence' instead */
  EvidenceChatSubmitted = 'evidence_chat_submitted',
  /** @deprecated Use MessageReceived with source: 'evidence' instead */
  EvidenceChatCompleted = 'evidence_chat_completed',
  /** @deprecated Use MessageFailed with source: 'evidence' instead */
  EvidenceChatError = 'evidence_chat_error',
  /** @deprecated Use MessageAborted with source: 'evidence' instead */
  EvidenceChatAborted = 'evidence_chat_aborted',
  /** Fired when a user clicks an inline `<heidi-button>` CTA in an Evidence chat response. */
  EvidenceChatCtaClicked = 'evidence_chat_cta_clicked',
  // Evidence - Onboarding events
  EvidenceOnboardingViewed = 'evidence_onboarding_viewed',
  EvidenceOnboardingSaved = 'evidence_onboarding_saved',
  EvidenceOnboardingSkipped = 'evidence_onboarding_skipped',
  EvidenceOnboardingDismissed = 'evidence_onboarding_dismissed',
  // Evidence - Settings events
  EvidenceLanguageChanged = 'evidence_language_changed',
  EvidenceSpecialtyChanged = 'evidence_specialty_changed',
  EvidenceJurisdictionChanged = 'evidence_jurisdiction_changed',
  // Evidence - Toast events
  EvidenceToastShown = 'evidence_toast_shown',
  CreditsConsumed = 'credits_consumed',
  // Evidence - UI toggle events
  /** @deprecated Use PanelOpened/PanelClosed with panel_type: 'source_control' instead */
  EvidenceSourceControlToggled = 'evidence_source_control_toggled',
  /** @deprecated Use ToggleUpdated with toggle_type: 'evidence_live_suggestions' instead */
  EvidenceLiveSuggestionsToggled = 'evidence_live_suggestions_toggled',
  // Evidence - Experience Feedback events
  EvidenceExperienceFeedbackViewed = 'evidence_experience_feedback_viewed',
  EvidenceExperienceFeedbackRatingSubmitted = 'evidence_experience_feedback_rating_submitted',
  EvidenceExperienceFeedbackSubmitted = 'evidence_experience_feedback_submitted',
  EvidenceExperienceFeedbackSnoozed = 'evidence_experience_feedback_snoozed',
  EvidenceExperienceFeedbackDismissed = 'evidence_experience_feedback_dismissed',

  // CPD
  CPDPageViewed = 'cpd_page_viewed',
  CPDExported = 'cpd_exported',
  CPDDraftCreated = 'cpd_draft_created',
  CPDDraftResumed = 'cpd_draft_resumed',
  CPDDraftDeleted = 'cpd_draft_deleted',

  // Chat Context Linking
  ChatContextAddSessionClicked = 'chat_context_add_session_clicked',
  ChatContextAttachPatientClicked = 'chat_context_attach_patient_clicked',
  ChatContextAddFileClicked = 'chat_context_add_file_clicked',
  ChatMessageSentWithContext = 'chat_message_sent_with_context',

  TemplateSelected = 'template_selected',
  NoteSynced = 'note_synced',
  NoteSyncFailed = 'note_sync_failed',
  PushWarningViewed = 'push_warning_viewed',
  PushWarningResolved = 'push_warning_resolved',
  SectionLinked = 'section_linked',
  DeeplinkTriggered = 'deeplink_triggered',

  TokenGenerated = 'token_generated',

  // Haiku Integration
  HaikuTabChanged = 'haiku_tab_changed',

  // Export Settings
  ExportSettingToggled = 'export_setting_toggled',
  ExportContentSaved = 'export_content_saved',

  // Integrations
  IntegrationCreated = 'integration_created',
  IntegrationDataCompleted = 'integration_data_completed',
  IntegrationDataFailed = 'integration_data_failed',
  IntegrationDataRetrieved = 'integration_data_retrieved',
  IntegrationPatientSelected = 'integration_patient_selected',
  IntegrationSessionCreated = 'integration_session_created',
  IntegrationUpdated = 'integration_updated',
  IntegrationUserSearched = 'integration_user_searched',
  IntegrationWidgetAuthStepErrored = 'integration_widget_auth_step_errored',
  IntegrationWidgetAuthStepMisc = 'integration_widget_auth_step_misc',
  GdtFileParsed = 'gdt_file_parsed',

  // Chronicle Device
  HardwareDeviceScanStarted = 'hardware_device_scan_started',
  HardwareDeviceConnected = 'hardware_device_connected',
  HardwareDeviceDisconnected = 'hardware_device_disconnected',
  NativeFirmwareUpgradeStart = 'native_firmware_upgrade_start',
  NativeFirmwareUpgradeComplete = 'native_firmware_upgrade_complete',
  NativeFirmwareUpgradeFailed = 'native_firmware_upgrade_failed',
  HardwareBulkSyncStarted = 'hardware_bulk_sync_started',
  HardwareBulkSyncCompleted = 'hardware_bulk_sync_completed',
  HardwareBulkSyncFailed = 'hardware_bulk_sync_failed',
  // Remote hardware session/file tracing (iOS parity)
  HardwareSessionCreated = 'hardware_session_created',
  HardwareFileDecryptionSkipped = 'hardware_file_decryption_skipped',
  HardwareFileDecryptionSuccess = 'hardware_file_decryption_success',
  HardwareFileDecryptionFailed = 'hardware_file_decryption_failed',
  /** Firmware-CRC verdict before a bulk-sync device delete (iOS parity). */
  HardwareBulkSyncFileCrcChecked = 'hardware_bulk_sync_file_crc_checked',

  // Hardware (iOS parity — APP-4173)
  HardwareSetupStarted = 'hardware_setup_started',
  HardwareBluetoothPermissionResult = 'hardware_bluetooth_permission_result',
  HardwareBluetoothSettingsOpened = 'hardware_bluetooth_settings_opened',
  HardwareDeviceConnectionFailed = 'hardware_device_connection_failed',
  HardwareDeviceNotFound = 'hardware_device_not_found',
  HardwareDeviceAlreadyPaired = 'hardware_device_already_paired',
  HardwareDeviceLinked = 'hardware_device_linked',
  HardwareDeviceLinkFailed = 'hardware_device_link_failed',
  HardwareSetupCompleted = 'hardware_setup_completed',
  HardwareSetupStepViewed = 'hardware_setup_step_viewed',
  HardwareInputModeSelected = 'hardware_input_mode_selected',
  HardwareRecordingStart = 'hardware_recording_start',
  HardwareRecordingStartFailed = 'hardware_recording_start_failed',
  HardwareRecordingStartBlocked = 'hardware_recording_start_blocked',
  /** Catalogue event (snowplow#377) — filter Amplitude by `device_action`. */
  DeviceEvent = 'device_event',
  /** Catalogue event — filter by `process_type`. */
  ProcessLifecycle = 'process_lifecycle',
  HardwareRecordingFirstChunkUploaded = 'hardware_recording_first_chunk_uploaded',
  HardwareRecordingChunkUploadFailed = 'hardware_recording_chunk_upload_failed',
  HardwareRecordingStop = 'hardware_recording_stop',
  HardwareRecordingStopFailed = 'hardware_recording_stop_failed',
  HardwareRecordingPause = 'hardware_recording_pause',
  HardwareRecordingPauseFailed = 'hardware_recording_pause_failed',
  HardwareRecordingResume = 'hardware_recording_resume',
  HardwareRecordingResumeFailed = 'hardware_recording_resume_failed',
  HardwareBleReconnected = 'hardware_ble_reconnected',
  HardwareDeviceRecordingStopped = 'hardware_device_recording_stopped',
  // A.3 Live Recovery (APP-4934)
  HardwareRecordingInterrupted = 'hardware_recording_interrupted',
  HardwareRecoveryCompleted = 'hardware_recovery_completed',
  HardwareRecoveryFailed = 'hardware_recovery_failed',
  // Lane-A bind on reconnect (APP-7875) — distinct from the Lane-B bulk-sync
  // completion signal (HardwareRecoveryCompleted) above.
  HardwareRecordingRecovered = 'hardware_recording_recovered',
  // A.4 Connection / Reconnect (APP-4934)
  HardwareDeviceReconnectAttempt = 'hardware_device_reconnect_attempt',
  HardwareDeviceReconnectTimeout = 'hardware_device_reconnect_timeout',
  // A.6 Permission / Transport fallback (APP-4934)
  HardwareTransportFallback = 'hardware_transport_fallback',
  // A.9 Error handling (APP-4934)
  HardwareErrorShown = 'hardware_error_shown',
  HardwareErrorRecoveryActionTaken = 'hardware_error_recovery_action_taken',
  HardwareBulkSyncTriggered = 'hardware_bulk_sync_triggered',
  HardwareBulkSyncResumed = 'hardware_bulk_sync_resumed',
  HardwareBulkSyncCancelled = 'hardware_bulk_sync_cancelled',
  HardwareBulkSyncPhaseChanged = 'hardware_bulk_sync_phase_changed',
  HardwareBulkSyncFileFailed = 'hardware_bulk_sync_file_failed',
  HardwareBulkSyncFileRetry = 'hardware_bulk_sync_file_retry',
  HardwareBulkSyncTaskFailed = 'hardware_bulk_sync_task_failed',
  HardwareWifiTransferRequested = 'hardware_wifi_transfer_requested',
  HardwareWifiSetupStarted = 'hardware_wifi_setup_started',
  HardwareWifiSetupCompleted = 'hardware_wifi_setup_completed',
  HardwareWifiSetupFailed = 'hardware_wifi_setup_failed',
  NativeFirmwareUpdateTapped = 'native_firmware_update_tapped',
  NativeFirmwareUpdateCheckStart = 'native_firmware_update_check_start',
  NativeFirmwareUpdateCheckFailed = 'native_firmware_update_check_failed',
  NativeFirmwareUpdateAvailable = 'native_firmware_update_available',
  NativeFirmwareUpToDate = 'native_firmware_up_to_date',
  NativeFirmwareDownloadStart = 'native_firmware_download_start',
  NativeFirmwareDownloadComplete = 'native_firmware_download_complete',
  NativeFirmwareDownloadFailed = 'native_firmware_download_failed',
  // A.7 OTA stage events (APP-4934)
  NativeFirmwareBatteryCheckFailed = 'native_firmware_battery_check_failed',
  NativeFirmwareUploadStart = 'native_firmware_upload_start',
  NativeFirmwareUploadComplete = 'native_firmware_upload_complete',
  NativeFirmwareUploadFailed = 'native_firmware_upload_failed',
  NativeFirmwareApplyStart = 'native_firmware_apply_start',
  NativeFirmwareApplyComplete = 'native_firmware_apply_complete',
  NativeFirmwareApplyFailed = 'native_firmware_apply_failed',
  NativeFirmwarePostUpdateReconnected = 'native_firmware_post_update_reconnected',
  HardwareDetailViewed = 'hardware_detail_viewed',
  HardwareDeviceRemoveTapped = 'hardware_device_remove_tapped',
  HardwareDeviceRemoved = 'hardware_device_removed',
  HardwareDeviceRemoveFailed = 'hardware_device_remove_failed',
  HardwareUsbModeToggled = 'hardware_usb_mode_toggled',
  HardwareUsbModeToggleFailed = 'hardware_usb_mode_toggle_failed',
  HardwareUsbAccessDenied = 'hardware_usb_access_denied',
  HardwareRetryConnectionTapped = 'hardware_retry_connection_tapped',
  HardwareLowBatteryShown = 'hardware_low_battery_shown',
  HardwareLearnMoreTapped = 'hardware_learn_more_tapped',
  HardwareContactSupportTapped = 'hardware_contact_support_tapped',

  // Chronicle bulk-sync SQLite migrations (APP-4647)
  HardwareBulkSyncDbMigrationApplied = 'hardware_bulk_sync_db_migration_applied',
  HardwareBulkSyncDbMigrationFailed = 'hardware_bulk_sync_db_migration_failed',

  // Contact Sales
  ContactSalesFormOpened = 'contact_sales_form_opened',
  ContactSalesFormSubmitted = 'contact_sales_form_submitted',
  // Demo Flow
  DemoStarted = 'demo_started',
  DemoCompleted = 'demo_completed',
  CoachMarkViewed = 'coach_mark_viewed',
  CoachMarkActionTaken = 'coach_mark_action_taken',
  FirstDocumentCreated = 'first_document_created',

  // Library
  LibraryDocumentUploaded = 'library_document_uploaded',
  LibraryCollectionCreated = 'library_collection_created',
  LibraryUploadNudgeClicked = 'library_upload_nudge_clicked',
  LibraryLinkClicked = 'library_link_clicked',
  LibraryDocumentAddedToCollection = 'library_document_added_to_collection',
  LibraryDocumentScopeChanged = 'library_document_scope_changed',

  // ── Amplitude Migration: New Events ─────────────────────────────────────

  // Session (new)
  SessionDeleted = 'session_deleted',
  TranscriptDeleted = 'transcript_deleted',
  PersonalizationUpdated = 'personalization_updated',

  // Document (new)
  DocumentCopied = 'document_copied',
  DocumentShared = 'document_shared',
  DocumentViewed = 'document_viewed',
  DocumentDeleted = 'document_deleted',
  DocumentSynced = 'document_synced',
  /** A rendered artifact was emailed to one or more recipients. */
  DocumentEmailed = 'document_emailed',
  /** A tab stopped being active — carries how long it was on screen. */
  SessionTabViewed = 'session_tab_viewed',
  /** The active tab changed within a session. */
  SessionTabSwitched = 'session_tab_switched',
  /** A session-detail Resume began capturing again (remote or app mic). */
  SessionDetailResumeStarted = 'session_detail_resume_started',

  // Notes (new)
  NoteStarted = 'note_started',
  /**
   * Fired when note generation fails (initial generation, regeneration, or
   * template change). Pairs with `note_started`.
   */
  NoteFailed = 'note_failed',
  NoteAutoRetryDecided = 'note_auto_retry_decided',
  NoteFailurePresented = 'note_failure_presented',
  NoteFailureOverlayCleared = 'note_failure_overlay_cleared',
  /**
   * Reserved for tracking failures when a note is pushed to an EHR. Currently
   * NOT dispatched from the FE — EHR push/sync failures route through
   * `note_sync_failed`. Do NOT reuse this for note generation errors; use
   * `NoteFailed` instead.
   */
  NotePushFailed = 'note_push_failed',
  /** A note/template section was created (convert-selection or Add section). */
  SectionCreated = 'section_created',
  /** A note/template section was deleted by the user (not draft auto-cleanup). */
  SectionDeleted = 'section_deleted',
  /** A note editor was rendered for a session (which editor variant). */
  NoteEditorRendered = 'note_editor_rendered',
  /** First content-changing edit of a session's consult note. */
  NoteEditStarted = 'note_edit_started',

  // Templates (new)
  TemplateFailed = 'template_failed',
  TemplateCompleted = 'template_completed',
  TemplateViewed = 'template_viewed',
  TemplateDeleted = 'template_deleted',
  TemplateSearched = 'template_searched',
  TemplateQualityGate = 'template_quality_gate',
  TemplateConverted = 'template_converted',

  // Tasks
  TaskCreated = 'task_created',
  TaskCompleted = 'task_completed',
  TaskDeleted = 'task_deleted',
  TaskUpdated = 'task_updated',
  TaskViewed = 'task_viewed',
  TaskListViewed = 'task_list_viewed',
  TaskNotificationClicked = 'task_notification_clicked',
  TaskDelegated = 'task_delegated',

  // Chat & Evidence (new)
  MessageStopped = 'message_stopped',
  EvidenceClicked = 'evidence_clicked',
  FeatureToggled = 'feature_toggled',

  // Survey
  SurveyViewed = 'survey_viewed',
  SurveySubmitted = 'survey_submitted',
  SurveyUpdated = 'survey_updated',

  // Team
  TeamMemberAccepted = 'team_member_accepted',
  TeamMemberCreated = 'team_member_created',
  TeamMemberDeleted = 'team_member_deleted',
  TeamCreated = 'team_created',
  TeamUpdated = 'team_updated',
  TeamEvidenceAccessUpdated = 'team_evidence_access_updated',
  TeamHeidiIiAccessUpdated = 'team_heidi_ii_access_updated',

  // Integrations (new)
  IntegrationFailed = 'integration_failed',
  IntegrationLinked = 'integration_linked',
  IntegrationStarted = 'integration_started',
  IntegrationCompleted = 'integration_completed',
  IntegrationSubmitted = 'integration_submitted',
  IntegrationUnlinked = 'integration_unlinked',
  PatientSearched = 'patient_searched',
  EhrStarted = 'ehr_started',

  // Audio
  AudioUploaded = 'audio_uploaded',
  AudioFailed = 'audio_failed',
  AudioStopped = 'audio_stopped',
  /** Desktop stop exhausted every native stop and a blocking failure was shown. */
  RecordingStopFailed = 'recording_stop_failed',
  /** Audio warning shown: in-recording silent capture, or post-recording low speech rate. */
  AudioWarningViewed = 'audio_warning_viewed',
  /** APP-7915 WS4: user clicked the warning's one-click fix. */
  AudioWarningAccepted = 'audio_warning_accepted',

  // Mini-mode (PiP recording indicator)
  MiniModeOpened = 'mini_mode_opened',
  MiniModeClosed = 'mini_mode_closed',
  MiniModeControlClicked = 'mini_mode_control_clicked',
  MiniModeControlAttempted = 'mini_mode_control_attempted',
  MiniModeNotepadEdited = 'mini_mode_notepad_edited',
  MiniModePreferenceChanged = 'mini_mode_preference_changed',
  NudgeProtocolsToggled = 'nudge_protocols_toggled',

  // Context (new)
  ContextUploaded = 'context_uploaded',
  ContextFailed = 'context_failed',
  ContextDeleted = 'context_deleted',
  ContextUpdated = 'context_updated',

  // Snippets
  SnippetCreated = 'snippet_created',
  SnippetUpdated = 'snippet_updated',
  SnippetDeleted = 'snippet_deleted',
  SnippetUploaded = 'snippet_uploaded',

  // Account & User (new)
  AccountDeleted = 'account_deleted',
  HelpViewed = 'help_viewed',
  AuthenticationClicked = 'authentication_clicked',
  UserUpdated = 'user_updated',
  UserStarted = 'user_started',
  UserClicked = 'user_clicked',
  ApplicationInstalled = 'application_install',

  // Feedback (new)
  FeedbackUpdated = 'feedback_updated',

  // Onboarding (new)
  OnboardingUpdated = 'onboarding_updated',

  // Diarisation
  DiarisedTranscriptViewed = 'diarised_transcript_viewed',
  SpeakerAttributionUpdated = 'speaker_attribution_updated',

  // Settings
  SettingsUpdated = 'settings_updated',
  /**
   * Fired when the global Settings modal opens — either explicitly via a UI
   * entry point or implicitly via URL hydration / legacy-route redirect.
   * `source` discriminates between them.
   */
  SettingsModalOpened = 'settings_modal_opened',
  /** Fired when the user navigates from one settings section to another. */
  SettingsModalSectionChanged = 'settings_modal_section_changed',
  /** Fired when the user closes the modal (X / Esc / click-outside / back). */
  SettingsModalClosed = 'settings_modal_closed',
  /**
   * Fired when the modal is asked to render an unknown route (typically a
   * stale deep link / removed section). Pairs with an auto-redirect to the
   * last-known route.
   */
  SettingsModalUnknownRoute = 'settings_modal_unknown_route',

  // Appearance / Theme
  /** Fired whenever the user changes their Light / Dark / System preference. */
  AppearanceModeChanged = 'appearance_mode_changed',

  // Export & Notification (new)
  ExportUpdated = 'export_updated',
  NotificationUpdated = 'notification_updated',

  // Referral
  ReferralCompleted = 'referral_completed',
  ReferralShared = 'referral_shared',
  ReferralViewed = 'referral_viewed',
  ReferralCreated = 'referral_created',

  // Product Tour
  ProductTourViewed = 'product_tour_viewed',

  // Errors
  ErrorViewed = 'error_viewed',

  // Community
  CommunityViewed = 'community_viewed',
  CommunitySearched = 'community_searched',
  CommunityCreated = 'community_created',

  // Entity Verification
  VerificationStarted = 'verification_started',
  VerificationCompleted = 'verification_completed',
  VerificationFailed = 'verification_failed',
  VerificationSuggestedFixApplied = 'verification_suggested_fix_applied',
  VerificationFeedbackSubmitted = 'verification_feedback_submitted',
  VerificationOverallFeedbackSubmitted = 'entity_verification_overall_feedback_submitted',
  VerificationAutoTriggered = 'verification_auto_triggered',
  VerificationAutoPopoverShown = 'verification_auto_popover_shown',
  VerificationAutoPopoverAction = 'verification_auto_popover_action',
  VerificationAutoModeToggled = 'verification_auto_mode_toggled',

  // Direct Sharing
  ShareButtonClicked = 'share_button_clicked',
  ShareModalOpened = 'share_modal_opened',
  ShareMemberSearched = 'share_member_searched',
  ShareMemberAdded = 'share_member_added',
  ShareLinkGenerated = 'share_link_generated',
  ShareLinkCopied = 'share_link_copied',
  ShareSaved = 'share_saved',
  ShareAccessChanged = 'share_access_changed',
  ShareAccessRevoked = 'share_access_revoked',
  ShareEditRequested = 'share_edit_requested',
  ShareNotificationReceived = 'share_notification_received',
  ShareNotificationClicked = 'share_notification_clicked',
  ShareExternalConfirmed = 'share_external_confirmed',
  ShareExternalEmailAdded = 'share_external_email_added',
  ShareExternalEmailRemoved = 'share_external_email_removed',
  ShareExternalInlineOpened = 'share_external_inline_opened',
  ShareExternalInlineCancelled = 'share_external_inline_cancelled',
  ShareExternalRevealOpened = 'share_external_reveal_opened',
  ShareExternalLinkSendSubmitted = 'share_external_link_send_submitted',

  // Unauthenticated Session Link Share
  SessionLinkShareOtpVerified = 'session_link_share_otp_verified',
  SessionLinkShareOtpFailed = 'session_link_share_otp_failed',

  // Form fields (PDF template editor)
  FormTemplateFieldsUpdated = 'form_template_fields_updated',

  // Evidence Prompts Bar (GRO-2764)
  EvidencePromptsBarShown = 'evidence_prompts_bar_shown',
  EvidencePromptsBarDismissed = 'evidence_prompts_bar_dismissed',
  EvidencePromptsBarExpanded = 'evidence_prompts_bar_expanded',

  // Evidence Ask Heidi shortcuts + Sources picker
  EvidenceShortcutsOpened = 'evidence_shortcuts_opened',
  EvidenceShortcutRun = 'evidence_shortcut_run',
  EvidenceShortcutSaveToggled = 'evidence_shortcut_save_toggled',
  EvidenceShortcutSearchUsed = 'evidence_shortcut_search_used',
  EvidenceSourcesOpened = 'evidence_sources_opened',

  // Evidence Summary Suggestion (GRO-2767)
  EvidenceSummarySuggestionShown = 'evidence_summary_suggestion_shown',
  EvidenceSummarySuggestionDismissed = 'evidence_summary_suggestion_dismissed',
  EvidenceSummarySuggestionGenerated = 'evidence_summary_suggestion_generated',

  // Document suggestion banner (Evidence drafting from clinical tasks)
  DocumentSuggestionBannerShown = 'document_suggestion_banner_shown',
  DocumentSuggestionBannerDismissed = 'document_suggestion_banner_dismissed',
  DocumentSuggestionBannerCtaClicked = 'document_suggestion_banner_cta_clicked',
  DocumentSuggestionBannerCarouselNavigated = 'document_suggestion_banner_carousel_navigated',
  DocumentSuggestionBannerSuppressed = 'document_suggestion_banner_suppressed',

  // Source Recommendation Banner
  SourceRecommendationShown = 'source_recommendation_shown',
  SourceRecommendationDismissed = 'source_recommendation_dismissed',
  SourceRecommendationApplied = 'source_recommendation_applied',
  SourceRecommendationCustomised = 'source_recommendation_customised',

  // Comms (Calls + Inbox)
  CommsDashboardTabViewed = 'comms_dashboard_tab_viewed',
  CommsEntryPointClicked = 'comms_entry_point_clicked',
  CommsSetupStarted = 'comms_setup_started',
  CommsSetupStepCompleted = 'comms_setup_step_completed',
  CommsSetupStepAbandoned = 'comms_setup_step_abandoned',
  CommsSetupCompleted = 'comms_setup_completed',
  ObjectiveSelected = 'objective_selected',
  ObjectiveRemoved = 'objective_removed',
  ObjectiveConfigured = 'objective_configured',
  ObjectiveViewed = 'objective_viewed',
  InboxSessionStarted = 'inbox_session_started',
  InboxSessionEnded = 'inbox_session_ended',
  InboxItemViewed = 'inbox_item_viewed',
  InboxItemUpdated = 'inbox_item_updated',
  InboxFilterApplied = 'inbox_filter_applied',

  // Comms — KB Learning (Objectives tab)
  KbObjectivesTabViewed = 'kb_objectives_tab_viewed',
  KbScanButtonClicked = 'kb_scan_button_clicked',
  KbPreviewButtonClicked = 'kb_preview_button_clicked',
  KbApplyButtonClicked = 'kb_apply_button_clicked',
  KbApplySuccessViewed = 'kb_apply_success_viewed',

  // Letter Sender (recipient events match the published com.heidi
  // letter_recipient_* Snowplow schemas)
  LetterSenderModalOpened = 'letter_sender_modal_opened',
  LetterSenderModalClosed = 'letter_sender_modal_closed',
  LetterRecipientAdded = 'letter_recipient_added',
  LetterRecipientRemoved = 'letter_recipient_removed',
  LetterRecipientSearched = 'letter_recipient_searched',
  LetterSenderCcToggled = 'letter_sender_cc_toggled',
  LetterSenderFileUploaded = 'letter_sender_file_uploaded',
  LetterSenderFileRemoved = 'letter_sender_file_removed',
  LetterSenderFilePreviewed = 'letter_sender_file_previewed',
  LetterSenderAttachmentRejected = 'letter_sender_attachment_rejected',
  LetterSenderSent = 'letter_sender_sent',
  LetterSenderSendFailed = 'letter_sender_send_failed',
}

export type TrackingEventV2 =
  // UI
  | {
      event_name: TrackingEventName.UiInteraction;
      props: UiInteractionEventProps;
    }
  | {
      event_name: TrackingEventName.ButtonClicked;
      props: ButtonEventProps;
    }
  | {
      event_name: TrackingEventName.ScreenViewed;
      props: ScreenEventProps;
    }
  | {
      event_name: TrackingEventName.ModalViewed;
      props: ModalEventProps;
    }
  | {
      event_name: TrackingEventName.ModalDismissed;
      props: ModalDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.FieldSelected;
      props: FieldSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.PanelOpened;
      props: PanelEventProps;
    }
  | {
      event_name: TrackingEventName.PanelClosed;
      props: PanelEventProps;
    }
  | {
      event_name: TrackingEventName.ToggleUpdated;
      props: ToggleUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.FormTemplateFieldsUpdated;
      props: FormTemplateFieldsUpdatedEventProps;
    }
  // Chat (object_action pattern for Evidence)
  | {
      event_name: TrackingEventName.MessageSubmitted;
      props: MessageSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.MessageReceived;
      props: MessageReceivedEventProps;
    }
  | {
      event_name: TrackingEventName.MessageFailed;
      props: MessageFailedEventProps;
    }
  | {
      event_name: TrackingEventName.MessageAborted;
      props: MessageAbortedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatCreated;
      props: ChatCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatViewed;
      props: ChatViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatDeleted;
      props: ChatDeletedEventProps;
    }
  // Authentication
  | {
      event_name: TrackingEventName.AuthenticationCompleted;
      props: OnboardingAuthenticationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.ForcedLogout;
      props: ForcedLogoutEventProps;
    }

  // Notes
  | {
      event_name: TrackingEventName.NoteCreated;
      props: NoteCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteSelected;
      props: NoteSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.ImproveNotesTaskCompleted;
      props: ImproveNotesTaskCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.PostNoteExperienceCompleted;
      props: PostNoteExperienceCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteComparisonViewed;
      props: NoteComparisonViewedEventProps;
    }
  | {
      event_name: TrackingEventName.TrialDaysUnlocked;
      props: TrialDaysUnlockedEventProps;
    }
  | {
      event_name: TrackingEventName.SpotlightAdvanced;
      props: SpotlightAdvancedEventProps;
    }
  | {
      event_name: TrackingEventName.SpotlightClosed;
      props: SpotlightClosedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteDisplayed;
      props: NoteDisplayedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteSelectionSkipped;
      props: NoteSelectionSkippedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteUpdated;
      props: NoteUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteReverted;
      props: NoteRevertedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteContentPasted;
      props: NoteContentPastedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionCopied;
      props: SessionCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.CopySettingsUpdated;
      props: CopySettingsUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.AssistantPanelAction;
      props: AssistantPanelActionEventProps;
    }
  // Onboarding
  | {
      event_name: TrackingEventName.OnboardingStarted;
      props?: OnboardingStartedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingStepCompleted;
      props?: OnboardingStepCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingStepSkipped;
      props: OnboardingStepSkippedEventProps;
    }
  | {
      event_name: TrackingEventName.SignupPhoneFieldViewed;
      props: SignupPhoneFieldViewedEventProps;
    }
  | {
      event_name: TrackingEventName.OrganisationSurveySubmitted;
      props: OrganisationSurveySubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingCompleted;
      props?: OnboardingCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingTemplateStarted;
      props: OnboardingTemplateStartedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingTemplateCompleted;
      props: OnboardingTemplateCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingTemplateReviewed;
      props: OnboardingTemplateReviewedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingDocumentsSelected;
      props: OnboardingDocumentsSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingNoteStyleUpdated;
      props: OnboardingNoteStyleUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingEvidenceQueried;
      props: OnboardingEvidenceQueriedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingEvidenceAccepted;
      props: OnboardingEvidenceAcceptedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingEvidenceDiscarded;
      props: OnboardingEvidenceDiscardedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingDocumentCreated;
      props: OnboardingDocumentCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingLearnBasicsCompleted;
      props: OnboardingLearnBasicsCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingLearnBasicsSkipped;
      props: OnboardingLearnBasicsSkippedEventProps;
    }

  // Account
  | {
      event_name: TrackingEventName.AccountCreated;
      props?: undefined;
    }
  // Session
  | {
      event_name: TrackingEventName.SessionCreated;
      props: SessionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionViewed;
      props?: SessionViewedEventProps;
    }
  // Transcription
  | {
      event_name: TrackingEventName.TranscriptionStarted;
      props?: TranscriptionStartedEventProps;
    }
  | {
      event_name: TrackingEventName.TranscriptionStopped;
      props?: TranscriptionStoppedEventProps;
    }
  | {
      event_name: TrackingEventName.TranscriptionSkipped;
      props: TranscriptionSkippedEventProps;
    }
  // Live Transcription Indicator (Record Anywhere)
  | {
      event_name: TrackingEventName.ActiveSessionIndicatorClicked;
      props: ActiveSessionIndicatorClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ActiveSessionControlClicked;
      props: ActiveSessionControlClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ActiveSessionNavigation;
      props: ActiveSessionNavigationEventProps;
    }
  // Dictation
  | {
      event_name: TrackingEventName.DictationStarted;
      props: DictationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.DictationCompleted;
      props: DictationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.DictationErrored;
      props: DictationErroredEventProps;
    }
  | {
      event_name: TrackingEventName.LegacyInlineDictationCompleted;
      props: LegacyInlineDictationCompletedEventProps;
    }
  // Dictation CSAT
  | {
      event_name: TrackingEventName.DictationCsatSubmitted;
      props: DictationCsatSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.DictationCsatDismissed;
      props: DictationCsatDismissedEventProps;
    }
  // Experiments
  // Account

  // Session
  | {
      event_name: TrackingEventName.SessionViewed;
      props?: undefined;
    }
  // Transcription

  // Experiments
  | {
      event_name: TrackingEventName.ExperimentStarted;
      props: ExperimentProps;
    }
  // Monetization
  | {
      event_name: TrackingEventName.PaywallViewed;
      props: PaywallEventProps;
    }
  // Monetization
  | {
      event_name: TrackingEventName.CheckoutStarted;
      props?: CheckoutStartedEventProps;
    }
  | {
      event_name: TrackingEventName.CancelFlowOpened;
      props: CancelFlowOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.CancelReasonSelected;
      props: CancelReasonSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.CancelOfferShown;
      props: CancelOfferEventProps;
    }
  | {
      event_name: TrackingEventName.CancelOfferAccepted;
      props: CancelOfferEventProps;
    }
  | {
      event_name: TrackingEventName.CancelOfferDeclined;
      props: CancelOfferEventProps;
    }
  | {
      event_name: TrackingEventName.PaymentStarted;
      props?: PaymentEventProps;
    }
  | {
      event_name: TrackingEventName.TrialStarted;
      props: TrialEventProps;
    }
  | {
      event_name: TrackingEventName.TrialCompleted;
      props: TrialCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.SubscriptionCreated;
      props: SubscriptionCreatedEventProps;
    }
  // Compliance
  | {
      event_name: TrackingEventName.PaymentCompleted;
      props: PaymentEventProps;
    }

  // Compliance
  | {
      event_name: TrackingEventName.ComplianceCheck;
      props: ComplianceEventProps;
    }
  // Auth
  | {
      event_name: TrackingEventName.UserSignedIn;
      props?: UserSignedInEventProps;
    }
  | {
      event_name: TrackingEventName.UserSignedUp;
      props?: UserSignedUpEventProps;
    }
  | {
      event_name: TrackingEventName.SignupConversion;
      props?: SignupConversionEventProps;
    }
  | {
      event_name: TrackingEventName.UserSignedOut;
      props?: SignOutEventProps;
    }
  | {
      event_name: TrackingEventName.AuthManualLinkCopied;
      props: AuthManualControlEventProps;
    }
  | {
      event_name: TrackingEventName.AuthManualOpenClicked;
      props: AuthManualControlEventProps;
    }
  | {
      event_name: TrackingEventName.AuthFallbackUsed;
      props: AuthManualControlEventProps;
    }
  // Feedback
  | {
      event_name: TrackingEventName.FeedbackSubmitted;
      props: FeedbackSubmittedEventProps;
    }
  // Force Update
  | {
      event_name: TrackingEventName.ForceUpdateViewed;
      props: ForceUpdateViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ForceUpdateDeferred;
      props: ForceUpdateDeferredEventProps;
    }
  | {
      event_name: TrackingEventName.ForceUpdateInitiated;
      props: ForceUpdateInitiatedEventProps;
    }

  // Documents
  | {
      event_name: TrackingEventName.DocumentExported;
      props: DocumentExportedEventProps;
    }
  | {
      event_name: TrackingEventName.FormFillingDownloaded;
      props: FormFillingDownloadedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentCreated;
      props: DocumentEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentGenerated;
      props: DocumentEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentUpdated;
      props: DocumentUpdatedEventProps;
    }
  // Queued document templates
  | {
      event_name: TrackingEventName.DocumentTemplateQueueLimitReached;
      props: DocumentTemplateQueueLimitReachedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentTemplateQueueReordered;
      props: DocumentTemplateQueueReorderedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionStartedWithDocumentQueue;
      props: SessionStartedWithDocumentQueueEventProps;
    }
  | {
      event_name: TrackingEventName.QueuedDocumentGenerationStarted;
      props: QueuedDocumentGenerationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.QueuedDocumentGenerationFailed;
      props: QueuedDocumentGenerationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.QueuedDocumentGenerationCancelled;
      props: QueuedDocumentGenerationCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.QueuedDocumentTabTemplateSwapped;
      props: QueuedDocumentTabTemplateSwappedEventProps;
    }
  // Memory (Snippets & Replacements)
  | {
      event_name: TrackingEventName.SnippetsUploaded;
      props: SnippetsUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.ReplacementsUploaded;
      props: ReplacementsUploadedEventProps;
    }
  // Style Updates
  | {
      event_name: TrackingEventName.ScribeStyleUpdated;
      props: ScribeStyleUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.VoiceStyleUpdated;
      props: VoiceStyleUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.AmbientHeidiTriggered;
      props: import('@heidi/agents/contracts').AmbientHeidiTriggeredProps;
    }
  // Clinical Codes
  | {
      event_name: TrackingEventName.ClinicalCodeCopied;
      props: ClinicalCodeCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.ClinicalCodeClickable;
      props: ClinicalCodeClickableEventProps;
    }
  // Clinical Calculators
  | {
      event_name: TrackingEventName.CalculatorExecuted;
      props: CalculatorExecutedEventProps;
    }
  // Schedule
  | {
      event_name: TrackingEventName.ScheduleViewed;
      props: ScheduleViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleSynced;
      props: ScheduleSyncedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadStarted;
      props: ScheduleUploadStartedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadCompleted;
      props: ScheduleUploadCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadProcessingFailed;
      props?: ScheduleUploadProcessingFailedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadSessionsCreated;
      props: ScheduleUploadSessionsCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadPromoViewed;
      props: ScheduleUploadPromoViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ScheduleUploadPromoClicked;
      props: ScheduleUploadPromoClickedEventProps;
    }
  // Heidi Remote intro promo (GRO-2981)
  | {
      event_name: TrackingEventName.RemoteIntroPromoViewed;
      props: RemoteIntroPromoViewedEventProps;
    }
  | {
      event_name: TrackingEventName.RemoteIntroPromoClicked;
      props: RemoteIntroPromoClickedEventProps;
    }
  | {
      event_name: TrackingEventName.RemoteIntroPromoDismissed;
      props: RemoteIntroPromoDismissedEventProps;
    }
  // Session Search & Filter
  | {
      event_name: TrackingEventName.SessionSearchUsageBaseline;
      props: SessionSearchUsageBaselineEventProps;
    }
  | {
      event_name: TrackingEventName.SessionsFilterApplied;
      props: SessionsFilterAppliedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionsScopeChanged;
      props: SessionsScopeChangedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionsSortChanged;
      props: SessionsSortChangedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionsListPaginated;
      props: SessionsListPaginatedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionUpdated;
      props: SessionUpdatedEventProps;
    }
  // Offline desktop recording
  | {
      event_name: TrackingEventName.OfflineRecordingStarted;
      props: OfflineRecordingStartedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineRecordingQueued;
      props: OfflineRecordingQueuedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineRecordingDeleted;
      props: OfflineRecordingDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSyncCompleted;
      props: OfflineSyncCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSyncFailed;
      props: OfflineSyncFailedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSyncDeferred;
      props: OfflineSyncDeferredEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSessionDeleteQueued;
      props: OfflineSessionDeleteQueuedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSessionDeleteEnqueueFailed;
      props: OfflineSessionDeleteEnqueueFailedEventProps;
    }
  | {
      event_name: TrackingEventName.OfflineSessionDeleteReplayFailed;
      props: OfflineSessionDeleteReplayFailedEventProps;
    }
  // Patient
  | {
      event_name: TrackingEventName.ExtractionModalOpened;
      props: PatientExtractionModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ExtractionReviewed;
      props: PatientExtractionReviewedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummarySmsSendAttempted;
      props: PatientSummarySmsSendAttemptedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummarySmsSent;
      props: PatientSummarySmsSentEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummaryEmailSendAttempted;
      props: PatientSummaryEmailSendAttemptedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummaryEmailSent;
      props: PatientSummaryEmailSentEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSummaryCtaShown;
      props: PatientSummaryCtaShownEventProps;
    }
  | {
      event_name: TrackingEventName.PatientViewed;
      props: PatientViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientUpdated;
      props: PatientUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientCreated;
      props: PatientCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientLinked;
      props: PatientLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSelected;
      props: PatientSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientUnlinked;
      props: PatientUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientRecapSourceSessionClicked;
      props: PatientRecapSourceSessionClickedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientListViewed;
      props: PatientListViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientProfileOpened;
      props: PatientProfileOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientProfileTabViewed;
      props: PatientProfileTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSessionExpanded;
      props: PatientSessionExpandedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientFieldEdited;
      props: PatientFieldEditedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDocumentUploaded;
      props: PatientDocumentUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDocumentLinked;
      props: PatientDocumentLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDocumentUnlinked;
      props: PatientDocumentUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientRecapPopoverOpened;
      props: PatientRecapPopoverOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDirectoryEnableClicked;
      props: PatientDirectoryEnableClickedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDeleteClicked;
      props: PatientDeleteClickedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientDeleteBlocked;
      props: PatientDeleteBlockedEventProps;
    }
  | {
      event_name: TrackingEventName.PrechartViewed;
      props: PrechartViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PrechartSentToContext;
      props: PrechartSentToContextEventProps;
    }
  | {
      event_name: TrackingEventName.PatientContextToggleOn;
      props: PatientContextToggleOnEventProps;
    }
  | {
      event_name: TrackingEventName.PatientContextToggleOff;
      props: PatientContextToggleOffEventProps;
    }
  // Tab sharing (Telehealth)
  | {
      event_name: TrackingEventName.TabSharingDoNoShowMessageAgain;
      props?: undefined;
    }
  | {
      event_name: TrackingEventName.TabSharingNotAvailableModalShown;
      props?: undefined;
    }
  // Templates
  | {
      event_name: TrackingEventName.TemplateCreated;
      props: TemplateCreatedProps;
    }
  | {
      event_name: TrackingEventName.TemplateUpdated;
      props: TemplateUpdatedProps;
    }
  | {
      event_name: TrackingEventName.TemplateCreationRecordCreated;
      props: TemplateCreationRecordCreatedProps;
    }
  | {
      event_name: TrackingEventName.TemplateEditorUpdated;
      props: TemplateEditorUpdatedProps;
    }
  | {
      event_name: TrackingEventName.TemplateGeneration;
      props: TemplateGenerationProps;
    }
  | {
      event_name: TrackingEventName.TemplateOpened;
      props: TemplateOpenedProps;
    }
  | {
      event_name: TrackingEventName.TemplateSelected;
      props: TemplateSelectedEventProps;
    }
  // Book Demo
  | {
      event_name: TrackingEventName.BookDemoFormSubmitted;
      props: BookDemoFormSubmittedEventProps;
    }

  // Book Demo
  | {
      event_name: TrackingEventName.BookDemoCountrySelected;
      props: BookDemoCountrySelectedEventProps;
    }
  | {
      event_name: TrackingEventName.BookDemoCliniciansSelected;
      props: BookDemoCliniciansSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.BookDemoNextClicked;
      props?: BookDemoNextClickedEventProps;
    }
  // Onboarding meeting / webinar (HubSpot)
  | {
      event_name: TrackingEventName.MeetingSlotSelected;
      props: MeetingSlotSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.MeetingBookingSubmitted;
      props: MeetingBookingSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.MeetingBooked;
      props: MeetingBookedEventProps;
    }
  | {
      event_name: TrackingEventName.MeetingBookingFailed;
      props: MeetingBookingFailedEventProps;
    }
  | {
      event_name: TrackingEventName.MeetingFallbackToRequestDemo;
      props: MeetingFallbackToRequestDemoEventProps;
    }
  | {
      event_name: TrackingEventName.WebinarOptInChanged;
      props: WebinarOptInChangedEventProps;
    }
  | {
      event_name: TrackingEventName.WebinarTimeslotSelected;
      props: WebinarTimeslotSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.WebinarRegistrationSubmitted;
      props: WebinarRegistrationSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.SdrDemoOptInChanged;
      props: SdrDemoOptInChangedEventProps;
    }
  | {
      event_name: TrackingEventName.SdrDemoRequested;
      props: SdrDemoRequestedEventProps;
    }
  | {
      event_name: TrackingEventName.PhoneDemoBooked;
      props: PhoneDemoBookedEventProps;
    }
  | {
      event_name: TrackingEventName.EarlyMeetingChoiceSelected;
      props: EarlyMeetingChoiceSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.EarlyMeetingRegionNotSupported;
      props: EarlyMeetingRegionNotSupportedEventProps;
    }
  | {
      event_name: TrackingEventName.OnboardingDemoVariantViewed;
      props: OnboardingDemoVariantViewedEventProps;
    }
  // Locale selection
  | {
      event_name: TrackingEventName.CountrySelected;
      props: CountrySelectedEventProps;
    }
  | {
      event_name: TrackingEventName.LanguageSelected;
      props: LanguageSelectedEventProps;
    }
  // Context Files
  | {
      event_name: TrackingEventName.ContextFileUploadStarted;
      props: ContextFileUploadStartedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextFileUploaded;
      props: ContextFileUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextFileUploadFailed;
      props: ContextFileUploadFailedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextFileDeleted;
      props: ContextFileDeletedEventProps;
    }
  // Integrations
  | {
      event_name: TrackingEventName.NoteSynced;
      props: IntegrationNoteSyncedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteSyncFailed;
      props: IntegrationNoteSyncedEventProps;
    }
  | {
      event_name: TrackingEventName.PushWarningViewed;
      props: PushWarningViewedEventProps;
    }
  | {
      event_name: TrackingEventName.PushWarningResolved;
      props: PushWarningResolvedEventProps;
    }
  | {
      event_name: TrackingEventName.SectionLinked;
      props: SectionLinkedEventProps;
    }
  // Context (Previous Notes/Sessions linked to current session)
  | {
      event_name: TrackingEventName.ContextPreviewed;
      props: ContextPreviewedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextLinked;
      props: ContextLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextUnlinked;
      props: ContextUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.AutoLinkSessionsPreferenceChanged;
      props: AutoLinkSessionsPreferenceChangedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientAutoExtractPreferenceChanged;
      props: PatientAutoExtractPreferenceChangedEventProps;
    }
  | {
      event_name: TrackingEventName.LinkedSessionsSpotlightDismissed;
      props: LinkedSessionsSpotlightDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.DictationLogsSpotlightDismissed;
      props: DictationLogsSpotlightDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.DictateTourBannerCtaClicked;
      props: DictateTourBannerCtaClickedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientLinkingToggled;
      props: PatientLinkingToggledEventProps;
    }
  | {
      event_name: TrackingEventName.PatientLinkingTimeframeSelected;
      props: PatientLinkingTimeframeSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientLinkingTimeframeDaysChanged;
      props: PatientLinkingTimeframeDaysChangedEventProps;
    }
  // Evidence
  | {
      event_name: TrackingEventName.EvidenceCategoryClicked;
      props: EvidenceCategoryClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceVerbClicked;
      props: EvidenceVerbClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSuggestionClicked;
      props: EvidenceSuggestionClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSignUpWallViewed;
      props: EvidenceSignUpWallViewedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSourceClicked;
      props: EvidenceSourceClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceRequestAccessClicked;
      props: EvidenceRequestAccessClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceContactSalesClicked;
      props: EvidenceContactSalesClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceNhsContactSalesSubmitted;
      props: EvidenceNhsContactSalesSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceResponseFeedbackSubmitted;
      props: EvidenceResponseFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceReferenceFeedbackSubmitted;
      props: EvidenceReferenceFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceReferenceScrollClicked;
      props: EvidenceReferenceScrollClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceReferencesSectionToggled;
      props: EvidenceReferencesSectionToggledEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShareModalOpened;
      props: EvidenceShareModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShareLinkCopied;
      props: EvidenceShareLinkCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShareSocialClicked;
      props: EvidenceShareSocialClickedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSharedChatOpened;
      props: EvidenceSharedChatOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceCopyConfirmation;
      props: EvidenceCopyConfirmationEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceCopied;
      props: EvidenceCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceCheckOtherSourcesClicked;
      props: EvidenceCheckOtherSourcesClickedEventProps;
    }
  | {
      event_name: TrackingEventName.FollowUpQuestionClicked;
      props: FollowUpQuestionClickedEventProps;
    }
  // Evidence - Chat events
  | {
      event_name: TrackingEventName.EvidenceChatSubmitted;
      props: EvidenceChatSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceChatCompleted;
      props: EvidenceChatCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceChatError;
      props: EvidenceChatErrorEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceChatAborted;
      props: EvidenceChatAbortedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceChatCtaClicked;
      props: EvidenceChatCtaClickedEventProps;
    }
  // Evidence - Onboarding events
  | {
      event_name: TrackingEventName.EvidenceOnboardingViewed;
      props: EvidenceOnboardingViewedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceOnboardingSaved;
      props: EvidenceOnboardingSavedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceOnboardingSkipped;
      props: EvidenceOnboardingSkippedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceOnboardingDismissed;
      props: EvidenceOnboardingDismissedEventProps;
    }
  // Evidence - Settings events
  | {
      event_name: TrackingEventName.EvidenceLanguageChanged;
      props: EvidenceLanguageChangedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSpecialtyChanged;
      props: EvidenceSpecialtyChangedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceJurisdictionChanged;
      props: EvidenceJurisdictionChangedEventProps;
    }
  // Evidence - Toast events
  | {
      event_name: TrackingEventName.EvidenceToastShown;
      props: EvidenceToastShownEventProps;
    }
  | {
      event_name: TrackingEventName.CreditsConsumed;
      props: CreditsConsumedEventProps;
    }
  // Evidence - UI toggle events
  | {
      event_name: TrackingEventName.EvidenceSourceControlToggled;
      props: EvidenceSourceControlToggledEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceLiveSuggestionsToggled;
      props: EvidenceLiveSuggestionsToggledEventProps;
    }
  // Evidence - Experience Feedback events
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackViewed;
      props: EvidenceExperienceFeedbackViewedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackRatingSubmitted;
      props: EvidenceExperienceFeedbackRatingSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackSubmitted;
      props: EvidenceExperienceFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackSnoozed;
      props: EvidenceExperienceFeedbackSnoozedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceExperienceFeedbackDismissed;
      props: EvidenceExperienceFeedbackDismissedEventProps;
    }
  // CPD
  | {
      event_name: TrackingEventName.CPDPageViewed;
      props: CPDPageViewedEventProps;
    }
  | {
      event_name: TrackingEventName.CPDExported;
      props: CPDExportedEventProps;
    }
  | {
      event_name: TrackingEventName.CPDDraftCreated;
      props: CPDDraftCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.CPDDraftResumed;
      props: CPDDraftResumedEventProps;
    }
  | {
      event_name: TrackingEventName.CPDDraftDeleted;
      props: CPDDraftDeletedEventProps;
    }
  // Chat Context Linking
  | {
      event_name: TrackingEventName.ChatContextAddSessionClicked;
      props: ChatContextAddSessionClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatContextAttachPatientClicked;
      props: ChatContextAttachPatientClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatContextAddFileClicked;
      props: ChatContextAddFileClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ChatMessageSentWithContext;
      props: ChatMessageSentWithContextEventProps;
    }
  | {
      event_name: TrackingEventName.DeeplinkTriggered;
      props: DeeplinkTriggeredEventProps;
    }
  | {
      event_name: TrackingEventName.TokenGenerated;
      props: TokenGeneratedEventProps;
    }
  // Haiku Integration
  | {
      event_name: TrackingEventName.HaikuTabChanged;
      props: HaikuTabChangedEventProps;
    }
  // Integrations
  | {
      event_name: TrackingEventName.IntegrationCreated;
      props: IntegrationCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationDataCompleted;
      props: IntegrationDataCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationDataFailed;
      props: IntegrationDataFailedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationDataRetrieved;
      props: IntegrationDataRetrievedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationPatientSelected;
      props: IntegrationPatientSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationSessionCreated;
      props: IntegrationSessionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationUpdated;
      props: IntegrationUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationUserSearched;
      props: IntegrationUserSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationWidgetAuthStepErrored;
      props: IntegrationWidgetAuthStepErroredEventProps;
    }
  | {
      event_name: TrackingEventName.GdtFileParsed;
      props: GdtFileParsedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationWidgetAuthStepMisc;
      props: IntegrationWidgetAuthStepMiscEventProps;
    }
  // Export Settings
  | {
      event_name: TrackingEventName.ExportSettingToggled;
      props: ExportSettingToggledEventProps;
    }
  | {
      event_name: TrackingEventName.ExportContentSaved;
      props: ExportContentSavedEventProps;
    }
  // Chronicle Device
  | {
      event_name: TrackingEventName.HardwareDeviceScanStarted;
      props: HardwareDeviceScanStartedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceConnected;
      props: HardwareDeviceConnectedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceDisconnected;
      props: HardwareDeviceDisconnectedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpgradeStart;
      props: NativeFirmwareUpgradeStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpgradeComplete;
      props: NativeFirmwareUpgradeCompleteEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpgradeFailed;
      props: NativeFirmwareUpgradeFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncStarted;
      props: HardwareBulkSyncStartedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncCompleted;
      props: HardwareBulkSyncCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncFailed;
      props: HardwareBulkSyncFailedEventProps;
    }
  // Remote hardware session/file tracing (iOS parity)
  | {
      event_name: TrackingEventName.HardwareSessionCreated;
      props: HardwareSessionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareFileDecryptionSkipped;
      props: HardwareFileDecryptionSkippedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareFileDecryptionSuccess;
      props: HardwareFileDecryptionSuccessEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareFileDecryptionFailed;
      props: HardwareFileDecryptionFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncFileCrcChecked;
      props: HardwareBulkSyncFileCrcCheckedEventProps;
    }
  // Hardware (iOS parity — APP-4173)
  | {
      event_name: TrackingEventName.HardwareSetupStarted;
      props: HardwareSetupStartedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBluetoothPermissionResult;
      props: HardwareBluetoothPermissionResultEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBluetoothSettingsOpened;
      props: HardwareBluetoothSettingsOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceConnectionFailed;
      props: HardwareDeviceConnectionFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceNotFound;
      props: HardwareDeviceNotFoundEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceAlreadyPaired;
      props: HardwareDeviceAlreadyPairedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceLinked;
      props: HardwareDeviceLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceLinkFailed;
      props: HardwareDeviceLinkFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareSetupCompleted;
      props: HardwareSetupCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareSetupStepViewed;
      props: HardwareSetupStepViewedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareInputModeSelected;
      props: HardwareInputModeSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStart;
      props: HardwareRecordingStartEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStartFailed;
      props: HardwareRecordingStartFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStartBlocked;
      props: HardwareRecordingStartBlockedEventProps;
    }
  | {
      event_name: TrackingEventName.DeviceEvent;
      props: DeviceEventProps;
    }
  | {
      event_name: TrackingEventName.ProcessLifecycle;
      props:
        | import('@snowtype/snowplow').ProcessLifecycle
        | import('@heidi/agents/contracts').AmbientHeidiUtteranceLifecycleProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingFirstChunkUploaded;
      props: HardwareRecordingFirstChunkUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingChunkUploadFailed;
      props: HardwareRecordingChunkUploadFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStop;
      props: HardwareRecordingStopEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingStopFailed;
      props: HardwareRecordingStopFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingPause;
      props: HardwareRecordingPauseEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingPauseFailed;
      props: HardwareRecordingPauseFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingResume;
      props: HardwareRecordingResumeEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingResumeFailed;
      props: HardwareRecordingResumeFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBleReconnected;
      props: HardwareBleReconnectedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceRecordingStopped;
      props: HardwareDeviceRecordingStoppedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncTriggered;
      props: HardwareBulkSyncTriggeredEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncResumed;
      props: HardwareBulkSyncResumedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncCancelled;
      props: HardwareBulkSyncCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncPhaseChanged;
      props: HardwareBulkSyncPhaseChangedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncFileFailed;
      props: HardwareBulkSyncFileFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncFileRetry;
      props: HardwareBulkSyncFileRetryEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncTaskFailed;
      props: HardwareBulkSyncTaskFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingInterrupted;
      props: HardwareRecordingInterruptedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecoveryCompleted;
      props: HardwareRecoveryCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecoveryFailed;
      props: HardwareRecoveryFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRecordingRecovered;
      props: HardwareRecordingRecoveredEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceReconnectAttempt;
      props: HardwareDeviceReconnectAttemptEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceReconnectTimeout;
      props: HardwareDeviceReconnectTimeoutEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareTransportFallback;
      props: HardwareTransportFallbackEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareErrorShown;
      props: HardwareErrorShownEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareErrorRecoveryActionTaken;
      props: HardwareErrorRecoveryActionTakenEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareWifiTransferRequested;
      props: HardwareWifiTransferRequestedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpdateTapped;
      props: NativeFirmwareUpdateTappedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpdateCheckStart;
      props: NativeFirmwareUpdateCheckStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpdateCheckFailed;
      props: NativeFirmwareUpdateCheckFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpdateAvailable;
      props: NativeFirmwareUpdateAvailableEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUpToDate;
      props: NativeFirmwareUpToDateEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareDownloadStart;
      props: NativeFirmwareDownloadStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareDownloadComplete;
      props: NativeFirmwareDownloadCompleteEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareDownloadFailed;
      props: NativeFirmwareDownloadFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareBatteryCheckFailed;
      props: NativeFirmwareBatteryCheckFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUploadStart;
      props: NativeFirmwareUploadStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUploadComplete;
      props: NativeFirmwareUploadCompleteEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareUploadFailed;
      props: NativeFirmwareUploadFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareApplyStart;
      props: NativeFirmwareApplyStartEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareApplyComplete;
      props: NativeFirmwareApplyCompleteEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwareApplyFailed;
      props: NativeFirmwareApplyFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NativeFirmwarePostUpdateReconnected;
      props: NativeFirmwarePostUpdateReconnectedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDetailViewed;
      props: HardwareDetailViewedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceRemoveTapped;
      props: HardwareDeviceRemoveTappedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceRemoved;
      props: HardwareDeviceRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareDeviceRemoveFailed;
      props: HardwareDeviceRemoveFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareUsbModeToggled;
      props: HardwareUsbModeToggledEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareUsbModeToggleFailed;
      props: HardwareUsbModeToggleFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareUsbAccessDenied;
      props: HardwareUsbAccessDeniedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareRetryConnectionTapped;
      props: HardwareRetryConnectionTappedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareLowBatteryShown;
      props: HardwareLowBatteryShownEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareLearnMoreTapped;
      props: HardwareLearnMoreTappedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareContactSupportTapped;
      props: HardwareContactSupportTappedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareWifiSetupStarted;
      props: HardwareWifiSetupStartedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareWifiSetupCompleted;
      props: HardwareWifiSetupCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareWifiSetupFailed;
      props: HardwareWifiSetupFailedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncDbMigrationApplied;
      props: HardwareBulkSyncDbMigrationAppliedEventProps;
    }
  | {
      event_name: TrackingEventName.HardwareBulkSyncDbMigrationFailed;
      props: HardwareBulkSyncDbMigrationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.ContactSalesFormOpened;
      props: ContactSalesFormOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ContactSalesFormSubmitted;
      props: ContactSalesFormSubmittedEventProps;
    }
  // Demo Flow
  | {
      event_name: TrackingEventName.DemoStarted;
      props: Record<string, never>;
    }
  | {
      event_name: TrackingEventName.DemoCompleted;
      props: DemoCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.CoachMarkViewed;
      props: CoachMarkEventProps;
    }
  | {
      event_name: TrackingEventName.CoachMarkActionTaken;
      props: CoachMarkEventProps;
    }
  | {
      event_name: TrackingEventName.FirstDocumentCreated;
      props: FirstDocumentCreatedEventProps;
    }
  // Library
  | {
      event_name: TrackingEventName.LibraryDocumentUploaded;
      props: LibraryDocumentUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryCollectionCreated;
      props: LibraryCollectionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryUploadNudgeClicked;
      props: LibraryUploadNudgeClickedEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryLinkClicked;
      props: LibraryLinkClickedEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryDocumentAddedToCollection;
      props: LibraryDocumentAddedToCollectionEventProps;
    }
  | {
      event_name: TrackingEventName.LibraryDocumentScopeChanged;
      props: LibraryDocumentScopeChangedEventProps;
    }

  // ── Amplitude Migration: New Union Members ────────────────────────────────

  // Session (new)
  | {
      event_name: TrackingEventName.SessionDeleted;
      props: SessionDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.TranscriptDeleted;
      props: TranscriptDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.PersonalizationUpdated;
      props: PersonalizationUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMarkedAsContextOnly;
      props: SessionMarkedAsContextOnlyEventProps;
    }
  | {
      event_name: TrackingEventName.SessionLayoutSwitched;
      props: SessionLayoutSwitchedEventProps;
    }

  // Document (new)
  | {
      event_name: TrackingEventName.DocumentCopied;
      props: DocumentCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentShared;
      props: DocumentSharedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentViewed;
      props: DocumentViewedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentDeleted;
      props: DocumentDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSynced;
      props: DocumentSyncedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentEmailed;
      props: DocumentEmailedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionTabViewed;
      props: SessionTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionTabSwitched;
      props: SessionTabSwitchedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionDetailResumeStarted;
      props: SessionDetailResumeStartedEventProps;
    }

  // Notes (new)
  | {
      event_name: TrackingEventName.NoteStarted;
      props: NoteStartedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteFailed;
      props: NoteFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteAutoRetryDecided;
      props: NoteAutoRetryDecidedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteFailurePresented;
      props: NoteFailurePresentedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteFailureOverlayCleared;
      props: NoteFailureOverlayClearedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteGenerationStarted;
      props: NoteGenerationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteGenerationCompleted;
      props: NoteGenerationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteGenerationFailed;
      props: NoteGenerationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NotePushFailed;
      props: NotePushFailedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteCopied;
      props: NoteCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.SectionCreated;
      props: NoteSectionCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.SectionDeleted;
      props: NoteSectionDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteEditorRendered;
      props: NoteEditorRenderedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteEditStarted;
      props: NoteEditStartedEventProps;
    }
  | {
      event_name: TrackingEventName.NoteInsertApplied;
      props: NoteInsertAppliedEventProps;
    }

  // Templates (new)
  | {
      event_name: TrackingEventName.TemplateFailed;
      props: TemplateFailedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateCompleted;
      props: TemplateCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateViewed;
      props: TemplateViewedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateDeleted;
      props: TemplateDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateReverted;
      props: TemplateRevertedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateSearched;
      props: TemplateSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateQualityGate;
      props: TemplateQualityGateEventProps;
    }
  | {
      event_name: TrackingEventName.TemplateConverted;
      props: TemplateConvertedProps;
    }

  // Tasks
  | {
      event_name: TrackingEventName.TaskCreated;
      props: TaskCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskCompleted;
      props: TaskCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskDeleted;
      props: TaskDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskUpdated;
      props: TaskUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskViewed;
      props: TaskViewedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskListViewed;
      props: TaskListViewedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskNotificationClicked;
      props: TaskNotificationClickedEventProps;
    }
  | {
      event_name: TrackingEventName.TaskDelegated;
      props: TaskDelegatedEventProps;
    }

  // Chat & Evidence (new)
  | {
      event_name: TrackingEventName.MessageStopped;
      props: MessageStoppedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceClicked;
      props: EvidenceClickedEventProps;
    }
  | {
      event_name: TrackingEventName.FeatureToggled;
      props: FeatureToggledEventProps;
    }

  // Survey
  | {
      event_name: TrackingEventName.SurveyViewed;
      props: SurveyViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SurveySubmitted;
      props: SurveySubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.SurveyUpdated;
      props: SurveyUpdatedEventProps;
    }

  // Team
  | {
      event_name: TrackingEventName.TeamMemberAccepted;
      props: TeamMemberAcceptedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamMemberCreated;
      props: TeamMemberCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamMemberDeleted;
      props: TeamMemberDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamCreated;
      props: TeamCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamUpdated;
      props: TeamUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamEvidenceAccessUpdated;
      props: TeamEvidenceAccessUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.TeamHeidiIiAccessUpdated;
      props: TeamHeidiIiAccessUpdatedEventProps;
    }

  // Integrations (new)
  | {
      event_name: TrackingEventName.IntegrationFailed;
      props: IntegrationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationLinked;
      props: IntegrationLinkedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationStarted;
      props: IntegrationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationCompleted;
      props: IntegrationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationSubmitted;
      props: IntegrationSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.IntegrationUnlinked;
      props: IntegrationUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.PatientSearched;
      props: PatientSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.EhrStarted;
      props: EhrStartedEventProps;
    }

  // Audio
  | {
      event_name: TrackingEventName.AudioUploaded;
      props: AudioUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.AudioFailed;
      props: AudioFailedEventProps;
    }
  | {
      event_name: TrackingEventName.AudioStopped;
      props: AudioStoppedEventProps;
    }
  | {
      event_name: TrackingEventName.RecordingStopFailed;
      props: RecordingStopFailedEventProps;
    }
  | {
      event_name: TrackingEventName.AudioWarningViewed;
      props: AudioWarningEventProps;
    }
  | {
      event_name: TrackingEventName.AudioWarningAccepted;
      props: AudioWarningEventProps;
    }

  // Mini-mode (PiP recording indicator)
  | {
      event_name: TrackingEventName.MiniModeOpened;
      props: MiniModeOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModeClosed;
      props: MiniModeClosedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModeControlClicked;
      props: MiniModeControlClickedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModeControlAttempted;
      props: MiniModeControlAttemptedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModeNotepadEdited;
      props: MiniModeNotepadEditedEventProps;
    }
  | {
      event_name: TrackingEventName.MiniModePreferenceChanged;
      props: MiniModePreferenceChangedEventProps;
    }
  | {
      event_name: TrackingEventName.NudgeProtocolsToggled;
      props: NudgeProtocolsToggledEventProps;
    }

  // Context (new)
  | {
      event_name: TrackingEventName.ContextUploaded;
      props: ContextUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextFailed;
      props: ContextFailedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextDeleted;
      props: ContextDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.ContextUpdated;
      props: ContextUpdatedEventProps;
    }

  // Snippets
  | {
      event_name: TrackingEventName.SnippetCreated;
      props: SnippetCreatedEventProps;
    }
  | {
      event_name: TrackingEventName.SnippetUpdated;
      props: SnippetUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.SnippetDeleted;
      props: SnippetDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.SnippetUploaded;
      props: SnippetUploadedEventProps;
    }

  // Account & User (new)
  | {
      event_name: TrackingEventName.AccountDeleted;
      props: AccountDeletedEventProps;
    }
  | {
      event_name: TrackingEventName.HelpViewed;
      props: HelpViewedEventProps;
    }
  | {
      event_name: TrackingEventName.AuthenticationClicked;
      props: AuthenticationClickedEventProps;
    }
  | {
      event_name: TrackingEventName.UserUpdated;
      props: UserUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.UserStarted;
      props: UserStartedEventProps;
    }
  | {
      event_name: TrackingEventName.UserClicked;
      props: UserClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ApplicationInstalled;
      props: ApplicationInstalledEventProps;
    }

  // Feedback (new)
  | {
      event_name: TrackingEventName.FeedbackUpdated;
      props: FeedbackUpdatedEventProps;
    }

  // Onboarding (new)
  | {
      event_name: TrackingEventName.OnboardingUpdated;
      props: OnboardingUpdatedEventProps;
    }

  // Diarisation
  | {
      event_name: TrackingEventName.DiarisedTranscriptViewed;
      props: DiarisedTranscriptViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SpeakerAttributionUpdated;
      props: SpeakerAttributionUpdatedEventProps;
    }

  // Settings
  | {
      event_name: TrackingEventName.SettingsUpdated;
      props: SettingsUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.SettingsModalOpened;
      props: SettingsModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.SettingsModalSectionChanged;
      props: SettingsModalSectionChangedEventProps;
    }
  | {
      event_name: TrackingEventName.SettingsModalClosed;
      props: SettingsModalClosedEventProps;
    }
  | {
      event_name: TrackingEventName.SettingsModalUnknownRoute;
      props: SettingsModalUnknownRouteEventProps;
    }

  // Appearance / Theme
  | {
      event_name: TrackingEventName.AppearanceModeChanged;
      props: AppearanceModeChangedEventProps;
    }

  // Export & Notification (new)
  | {
      event_name: TrackingEventName.ExportUpdated;
      props: ExportUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.NotificationUpdated;
      props: NotificationUpdatedEventProps;
    }

  // Referral
  | {
      event_name: TrackingEventName.ReferralCompleted;
      props: ReferralCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.ReferralShared;
      props: ReferralSharedEventProps;
    }
  | {
      event_name: TrackingEventName.ReferralViewed;
      props: ReferralViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ReferralCreated;
      props: ReferralCreatedEventProps;
    }

  // Product Tour
  | {
      event_name: TrackingEventName.ProductTourViewed;
      props: ProductTourViewedEventProps;
    }

  // Errors
  | {
      event_name: TrackingEventName.OnboardingErrorViewed;
      props: OnboardingErrorViewedEventProps;
    }
  | {
      event_name: TrackingEventName.ErrorViewed;
      props: ErrorViewedEventProps;
    }

  // Community
  | {
      event_name: TrackingEventName.CommunityViewed;
      props: CommunityViewedEventProps;
    }
  | {
      event_name: TrackingEventName.CommunitySearched;
      props: CommunitySearchedEventProps;
    }
  | {
      event_name: TrackingEventName.CommunityCreated;
      props: CommunityCreatedEventProps;
    }

  // Direct Sharing
  | {
      event_name: TrackingEventName.ShareButtonClicked;
      props: ShareButtonClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareModalOpened;
      props: ShareModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareMemberSearched;
      props: ShareMemberSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareMemberAdded;
      props: ShareMemberAddedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareLinkGenerated;
      props: ShareLinkGeneratedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareLinkCopied;
      props: ShareLinkCopiedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareSaved;
      props: ShareSavedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareAccessChanged;
      props: ShareAccessChangedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareAccessRevoked;
      props: ShareAccessRevokedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareEditRequested;
      props: ShareEditRequestedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareNotificationReceived;
      props: ShareNotificationReceivedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareNotificationClicked;
      props: ShareNotificationClickedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalConfirmed;
      props: ShareExternalConfirmedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalEmailAdded;
      props: ShareExternalEmailAddedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalEmailRemoved;
      props: ShareExternalEmailRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalInlineOpened;
      props: ShareExternalInlineOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalInlineCancelled;
      props: ShareExternalInlineCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalRevealOpened;
      props: ShareExternalRevealOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.ShareExternalLinkSendSubmitted;
      props: ShareExternalLinkSendSubmittedEventProps;
    }

  // Unauthenticated Session Link Share
  | {
      event_name: TrackingEventName.SessionLinkShareOtpVerified;
      props: SessionLinkShareOtpVerifiedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionLinkShareOtpFailed;
      props: SessionLinkShareOtpFailedEventProps;
    }

  // Entity Verification
  | {
      event_name: TrackingEventName.VerificationStarted;
      props: VerificationStartedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationCompleted;
      props: VerificationCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationFailed;
      props: VerificationFailedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationSuggestedFixApplied;
      props: VerificationSuggestedFixAppliedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationFeedbackSubmitted;
      props: VerificationFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationOverallFeedbackSubmitted;
      props: VerificationOverallFeedbackSubmittedEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationAutoTriggered;
      props: VerificationAutoTriggeredEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationAutoPopoverShown;
      props: VerificationAutoPopoverShownEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationAutoPopoverAction;
      props: VerificationAutoPopoverActionEventProps;
    }
  | {
      event_name: TrackingEventName.VerificationAutoModeToggled;
      props: VerificationAutoModeToggledEventProps;
    }

  // Evidence Prompts Bar (GRO-2764)
  | {
      event_name: TrackingEventName.EvidencePromptsBarShown;
      props: EvidencePromptsBarShownEventProps;
    }
  | {
      event_name: TrackingEventName.EvidencePromptsBarDismissed;
      props: EvidencePromptsBarDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidencePromptsBarExpanded;
      props: EvidencePromptsBarExpandedEventProps;
    }

  // Evidence Ask Heidi shortcuts + Sources picker
  | {
      event_name: TrackingEventName.EvidenceShortcutsOpened;
      props: EvidenceShortcutsOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShortcutRun;
      props: EvidenceShortcutRunEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShortcutSaveToggled;
      props: EvidenceShortcutSaveToggledEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceShortcutSearchUsed;
      props: EvidenceShortcutSearchUsedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSourcesOpened;
      props: EvidenceSourcesOpenedEventProps;
    }

  // Evidence Summary Suggestion (GRO-2767)
  | {
      event_name: TrackingEventName.EvidenceSummarySuggestionShown;
      props: EvidenceSummarySuggestionShownEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSummarySuggestionDismissed;
      props: EvidenceSummarySuggestionDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.EvidenceSummarySuggestionGenerated;
      props: EvidenceSummarySuggestionGeneratedEventProps;
    }

  // Document suggestion banner
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerShown;
      props: DocumentSuggestionBannerShownEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerDismissed;
      props: DocumentSuggestionBannerDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerCtaClicked;
      props: DocumentSuggestionBannerCtaClickedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerCarouselNavigated;
      props: DocumentSuggestionBannerCarouselNavigatedEventProps;
    }
  | {
      event_name: TrackingEventName.DocumentSuggestionBannerSuppressed;
      props: DocumentSuggestionBannerSuppressedEventProps;
    }

  // Source Recommendation Banner
  | {
      event_name: TrackingEventName.SourceRecommendationShown;
      props: SourceRecommendationShownEventProps;
    }
  | {
      event_name: TrackingEventName.SourceRecommendationDismissed;
      props: SourceRecommendationDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.SourceRecommendationApplied;
      props: SourceRecommendationAppliedEventProps;
    }
  | {
      event_name: TrackingEventName.SourceRecommendationCustomised;
      props: SourceRecommendationCustomisedEventProps;
    }

  // Comms (Calls + Inbox)
  | {
      event_name: TrackingEventName.CommsDashboardTabViewed;
      props: CommsDashboardTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsEntryPointClicked;
      props: CommsEntryPointClickedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsSetupStarted;
      props: CommsSetupStartedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsSetupStepCompleted;
      props: CommsSetupStepCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsSetupStepAbandoned;
      props: CommsSetupStepAbandonedEventProps;
    }
  | {
      event_name: TrackingEventName.CommsSetupCompleted;
      props: CommsSetupCompletedEventProps;
    }
  | {
      event_name: TrackingEventName.ObjectiveSelected;
      props: ObjectiveSelectedEventProps;
    }
  | {
      event_name: TrackingEventName.ObjectiveRemoved;
      props: ObjectiveRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.ObjectiveConfigured;
      props: ObjectiveConfiguredEventProps;
    }
  | {
      event_name: TrackingEventName.ObjectiveViewed;
      props: ObjectiveViewedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxSessionStarted;
      props: InboxSessionStartedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxSessionEnded;
      props: InboxSessionEndedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxItemViewed;
      props: InboxItemViewedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxItemUpdated;
      props: InboxItemUpdatedEventProps;
    }
  | {
      event_name: TrackingEventName.InboxFilterApplied;
      props: InboxFilterAppliedEventProps;
    }

  // Comms — KB Learning (Objectives tab)
  | {
      event_name: TrackingEventName.KbObjectivesTabViewed;
      props: KbObjectivesTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.KbScanButtonClicked;
      props: KbScanButtonClickedEventProps;
    }
  | {
      event_name: TrackingEventName.KbPreviewButtonClicked;
      props: KbPreviewButtonClickedEventProps;
    }
  | {
      event_name: TrackingEventName.KbApplyButtonClicked;
      props: KbApplyButtonClickedEventProps;
    }
  | {
      event_name: TrackingEventName.KbApplySuccessViewed;
      props: KbApplySuccessViewedEventProps;
    }

  // Letter Sender
  | {
      event_name: TrackingEventName.LetterSenderModalOpened;
      props: LetterSenderModalOpenedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderModalClosed;
      props: LetterSenderModalClosedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterRecipientAdded;
      props: LetterSenderRecipientAddedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterRecipientRemoved;
      props: LetterSenderRecipientRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterRecipientSearched;
      props: LetterSenderProviderSearchedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderCcToggled;
      props: LetterSenderCcToggledEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderFileUploaded;
      props: LetterSenderFileUploadedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderFileRemoved;
      props: LetterSenderFileRemovedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderSent;
      props: LetterSenderSentEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderSendFailed;
      props: LetterSenderSendFailedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderFilePreviewed;
      props: LetterSenderFilePreviewedEventProps;
    }
  | {
      event_name: TrackingEventName.LetterSenderAttachmentRejected;
      props: LetterSenderAttachmentRejectedEventProps;
    }
  // Session Merge
  | {
      event_name: TrackingEventName.SessionMergeReviewViewed;
      props: SessionMergeReviewViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeConfirmed;
      props: SessionMergeConfirmedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeIgnored;
      props: SessionMergeIgnoredEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeReviewDismissed;
      props: SessionMergeReviewDismissedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeFailed;
      props: SessionMergeFailedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeTabViewed;
      props: SessionMergeTabViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeUnlinked;
      props: SessionMergeUnlinkedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeRelinked;
      props: SessionMergeRelinkedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeDissolvePromptViewed;
      props: SessionMergeDissolvePromptViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeDissolveConfirmed;
      props: SessionMergeDissolveConfirmedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeDissolveCancelled;
      props: SessionMergeDissolveCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeUnlinkPromptViewed;
      props: SessionMergeUnlinkPromptViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeUnlinkConfirmed;
      props: SessionMergeUnlinkConfirmedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeUnlinkCancelled;
      props: SessionMergeUnlinkCancelledEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeOnboardingViewed;
      props: SessionMergeOnboardingViewedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeOnboardingAccepted;
      props: SessionMergeOnboardingAcceptedEventProps;
    }
  | {
      event_name: TrackingEventName.SessionMergeOnboardingDismissed;
      props: SessionMergeOnboardingDismissedEventProps;
    };
